import React, { useState, useRef, useEffect, useMemo } from "react";
import { Button, Col, Form, Row, Modal } from "react-bootstrap";
import {
  faCloudUpload,
  faGear,
  faIndianRupeeSign,
  faPen,
  faPlus,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { useLocation, useParams } from "react-router-dom";
import infoImg from "../../Utils/assets/img/icons/info_icon.png";
import {
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../Utils/hooks/ApiHandler";
import useLocalStorageState from "use-local-storage-state";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import AddressModal from "./AddressModal";
import QuotationEditor from "./Quotationeditor";
import {
  customStyles,
  formatToINR,
  getModuleSeries,
} from "../../Utils/helpers/commonhelper";
import {
  DateField,
  MultipleOpt,
  PhoneNumberInput,
  SelectOpt,
  Text,
  Time,
} from "../../CommonComponents/FieldComp";
import { IconXboxX } from "@tabler/icons-react";
import { parsePhoneNumber } from "libphonenumber-js/min";
import { AuthorizationCard } from "../../CommonComponents/AuthorizationCard";
import { UpgradePlan } from "../../CommonComponents/UpgradePlan";
import { imageURL } from "../../Utils/constants/constant";
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials";
import Spinner from "../../Utils/helpers/SpinLoader";

// Helpers to normalize incoming values (object | string | number) to string IDs
const idString = (msg) =>
  z.preprocess(
    (val) => {
      if (val == null) return "";
      if (typeof val === "object") {
        if ("value" in val) return String(val.value ?? "");
        return "";
      }
      return String(val);
    },
    z.string().min(1, msg),
  );

const asDateString = (msg) =>
  z.preprocess(
    (val) => {
      if (val instanceof Date) return val.toISOString().split("T")[0]; // YYYY-MM-DD format
      return val;
    },
    z.string().min(1, msg),
  );

const CreateQuotationSchema = z.object({
  contact: idString("Contact name is required"),
  quotationNumber: z.string().min(1, "Quotation number is required"),
  reference: z.string().optional(),
  quoteDate: asDateString("Quote date is required"),
  expiryDate: asDateString("Expiry date is required"),
  leadOwnerId: idString("Lead owner is required"),
  deal: idString("Deal is required"),
  shortDescription: z.string().min(1, "Short description is required"),
  items: z
    .array(
      z.object({
        item: z.preprocess(
          (val) => {
            if (val == null) return null;
            if (typeof val === "string" || typeof val === "number") {
              const s = String(val);
              return { value: s, label: s };
            }
            return val;
          },
          z
            .object({
              value: z.string(),
              label: z.string(),
            })
            .nullable()
            .refine((val) => val !== null, { message: "Item is required" }),
        ),
        quantity: z.coerce.number().min(1, "Quantity must be at least 1"),
        rate: z.coerce.number().min(0, "Rate must be a positive number"),
        gst: z.coerce
          .number()
          .min(0, "GST must be a positive number")
          .optional(),
        discount: z.coerce
          .number()
          .min(0, "Discount must be a positive number")
          .optional(),
        discountType: z.string().optional(),
        description: z.string().optional(),
      }),
    )
    .min(1, "At least one item is required"),
  termsConditions: z.string().optional(),
  attachment: z.any().optional(),
  customerNote: z.string().optional(),
  specialDiscount: z.coerce
    .number()
    .min(0, "Discount must be a positive number")
    .optional(),
  shippingCharge: z.coerce
    .number()
    .min(0, "Shipping charge must be a positive number")
    .optional(),
  specialDiscountType: z.string().optional(),
  gstPercentage: z.coerce
    .number()
    .min(0, "GST must be a positive number")
    .optional(),
});

export const EditQuotation = ({ onClose }) => {

  // getting essentials for apis
  const { customer, setCustomer, headers, extras, navigate, customerId: customer_id, loginUserId, userType } = useGetEssentials()

  // rbac Permission object
  const [PERMISSION, _] = useLocalStorageState('permission')

  const { paramId } = useParams();
  const location = useLocation();
  const mode = location.state?.mode || "edit";
  const From = location.state?.from || "";

  const [leadOwner, setLeadOwner] = useState([]);
  const [contactOption, setContactOption] = useState([]);
  const [dealOption, setDealOptions] = useState([]);
  const [productOption, setProductOption] = useState([]);
  const [dealId, setDealId] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [productToCompaire, setProductToCompair] = useState([]);
  const [draftClick, setDraftClicked] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  const [isDragging, setIsDragging] = useState(false);
  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const fileInputRef = useRef(null);
  const [showTextareaIndex, setShowTextareaIndex] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedLeadOwner, setSelectedLeadOwner] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [quotationModule, setQuotationModule] = useState([]);
  const [revision, setRevision] = useState("");
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const newOptions = ["%", "₹"];
  const [selected, setSelected] = useState(newOptions[0]);
  const [open, setOpen] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [isOwnerSame, setIsOwnerSame] = useState(false);

  // Quotation rbac permissions
  // isquotation is the authorization permission for the respective module if its in the current subscription plan or not
  const isQuotation = PERMISSION.quotation?.isAuthorized;
  const quotationGrant = PERMISSION.quotation

  // function to set the dynamic fields for the dynamic fields
  const generateDynamicSchema = (fields) => {
    const schemaFields = {};

    fields.forEach((field) => {
      const fieldId = field.id;

      switch (field.label_type) {
        case "text":
        case "largeText":
        case "address":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;

        case "numerical":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;

        case "monetary":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.preprocess(
                (val) =>
                  val === "" || val === null || val === undefined
                    ? undefined
                    : Number(val),
                z
                  .number({
                    required_error: `${field.label_name} is required`,
                    invalid_type_error: "Please enter a valid number",
                  })
                  .min(0.01, "Amount must be positive"),
              )
              : z.preprocess(
                (val) =>
                  val === "" || val === null || val === undefined
                    ? null
                    : Number(val),
                z
                  .number()
                  .min(0, "Amount cannot be negative")
                  .optional()
                  .nullable(),
              );
          break;

        case "single-option":
        case "user":
        case "person":
        case "organization":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional().nullable().or(z.literal(""));
          break;

        case "date":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;

        case "date range":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z
                .object({
                  start: z.string().min(1, "Start date is required"),
                  end: z.string().min(1, "End date is required"),
                })
                .refine((data) => data.start <= data.end, {
                  message: "End date must be after start date",
                })
              : z
                .object({
                  start: z.string(),
                  end: z.string(),
                })
                .optional();
          break;

        case "time":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string().min(1, `${field.label_name} is required`)
              : z.string().optional();
          break;

        case "time-range":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.object({
                start: z.string().min(1, "Start time is required"),
                end: z.string().min(1, "End time is required"),
              })
              : z
                .object({
                  start: z.string(),
                  end: z.string(),
                })
                .optional();
          break;

        case "phone":
          schemaFields[fieldId] =
            field.is_required === "yes"
              ? z.string(z.string().min(1, "phone is requ"))
              : z.string().optional();
          break;
        case "multiple-option": // Added multiple-option schema
          schemaFields[fieldId] = // Changed from fieldName
            field.is_required === "yes"
              ? z.array(z.string()).min(1, `${field.label_name} is required`)
              : z.array(z.string()).optional();
          break;

        // case 'phone_multiple':
        //     schemaFields[fieldName] = field.is_required === "yes"
        //         ? z.array(z.object({
        //             phone: z.string().min(1, "Phone number is required"),
        //             phone_type: z.string(),
        //             country_code: z.string(),
        //             countryName: z.string()
        //         })).min(1, "At least one phone number is required")
        //         : z.array(z.object({
        //             phone: z.string(),
        //             phone_type: z.string(),
        //             country_code: z.string(),
        //             countryName: z.string()
        //         })).optional();
        //     break;

        default:
          schemaFields[fieldId] = z.any().optional();
      }
    });

    return z.object(schemaFields);
  };
  const [dynamicFields, setDynamicFields] = useState([]);
  // groped state to hold the dynamic group data
  const [grouped, setGrouped] = useState([]);
  // ungroped state to hold the dynamic group data
  const [ungrouped, setUnGrouped] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  // Memoize the combined schema to avoid unnecessary recalculations
  const finalSchema = useMemo(() => {
    if (dynamicFields.length === 0) {
      return CreateQuotationSchema;
    }

    // Combine grouped and ungrouped fields
    const allDynamicFields = [
      ...(grouped?.flatMap((g) => g.groupFields || []) || []),
      ...(ungrouped || []),
    ];

    if (allDynamicFields.length === 0) {
      return CreateQuotationSchema;
    }

    const dynamicSchema = generateDynamicSchema(allDynamicFields);

    // Merge static and dynamic schemas
    return CreateQuotationSchema.merge(dynamicSchema);
  }, [grouped, ungrouped, dynamicFields]);

  const discountTypeOptions = [
    { value: "%", label: "%" },
    { value: "₹", label: "₹" },
  ];

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
    watch,
    trigger,
    reset,
  } = useForm({
    resolver: zodResolver(finalSchema),
    defaultValues: {
      items: [
        {
          item: null,
          quantity: 1,
          rate: 0,
          gst: 0,
          discount: 0,
          discountType: "Percentage",
          description: "",
        },
      ],
      specialDiscountType: "%",
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "items",
  });

  // Watch for changes to items to trigger re-renders
  const items = watch("items");
  // phonje number validator
  const validatePhoneNumber = (rawNumber, countryCode) => {
    try {
      const phoneNumber = parsePhoneNumber(
        String(rawNumber),
        String(countryCode?.toUpperCase()),
      );
      const valid = phoneNumber?.isValid() || false;
      return valid;
    } catch (error) {
      return false;
    }
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setValue("attachment", selectedFile);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    handleFile(droppedFile);
  };
  useEffect(() => {
    if (mode === "clone") {
      reset({
        quotationNumber: "",
      });
      setRevision(""); // reset revision bhi
    }else if(mode === "cloneRe"){
      //  reset({
      //   quotationNumber: "",
      // });
    } else {
      reset({
        quotationNumber: data?.quotation_number || "",
      });
    }
  }, [data, mode, reset]);

  const fetchDataOfSpecificQuotation = async () => {
    try {
      const res = await GetRequest(
        `/quotation/fetchDataOfSpecificQuotation/${paramId}/${customer_id}/${loginUserId}`,
        { headers },
        extras,
      );

      if (res.status === "fail" || res.status !== "success") {
        return toast.error(res.message || "something went wrong");
      }
      if (res.status === "success") {
        setData(res?.data[0]);
        //    if (
        //   loginUserId === res?.data[0]?.owner_id &&
        //   customer?.customer_details?.user_type === "1"
        // ) {
        //   setIsOwnerSame(true)
        // } else {
        //   setIsOwnerSame(false)
        // }

        if (userType === "1") {
          setIsOwnerSame(true);
        } else if (loginUserId === res?.data[0]?.owner_id) {
          setIsOwnerSame(true);
        } else {
          setIsOwnerSame(false);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDataOfSpecificQuotation();
  }, [paramId]);

  const quoteDate = watch("quoteDate");

  const quoteExp = watch("expiryDate");
  const qDate = new Date(quoteDate);
  const eDate = new Date(quoteExp);

  useEffect(() => {
    if (quoteDate) {
      trigger("expiryDate");
    }
  }, [quoteDate, trigger, quoteExp]);

  const today = new Date().toISOString().split("T")[0];
  const tomorrow = new Date();

  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];

  useEffect(() => {
    if (mode === "clone") {
      const today = new Date().toISOString().split("T")[0];
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split("T")[0];

      setValue("quoteDate", today);
      setValue("expiryDate", tomorrowStr);
    }
  }, [mode, setValue]);
   
    useEffect(() => {
      if(mode === "cloneRe"){
           async function fetchQuotation() {
      const data = await getModuleSeries(customer_id, "quotation");
      setQuotationModule(`${data.prefix}` + "-" + `${data.incremental_number}`);
      setValue(
        "quotationNumber",
        `${data.prefix}` + "-" + `${data.incremental_number}`,
      );
    }
    fetchQuotation();
      }

  }, []);
  console.log(quotationModule,"module")
  const displayQuotationNumber =
    mode === "clone"
      ? `${data?.quotation_number}${revision ? "_" + revision : ""}`
      : mode === "cloneRe" ? `${quotationModule|| ""}`: `${data?.quotation_number}`;

  const handleDraftClick = () => {
    setDraftClicked(true);
  };

  const calculateGroupedGST = () => {
    const gstMap = {};
    fields.forEach((_, index) => {
      const gst = getValues(`items.${index}.gst`) || 0;
      if (gst > 0) {
        const baseAmount = calculateItemAmount(index);
        const discount = calculateSpecialDiscountForItem(index);
        const netAmount = baseAmount - discount;

        const gstAmount = (netAmount * gst) / 100;
        gstMap[gst] = (gstMap[gst] || 0) + gstAmount;
      }
    });
    return gstMap;
  };

  const calculateSpecialDiscountForItem = (index) => {
    const subTotal = fields.reduce(
      (sum, _, i) => sum + calculateItemAmount(i),
      0,
    );
    const itemAmount = calculateItemAmount(index);

    const specialDiscount = getValues("specialDiscount") || 0;
    const specialDiscountType = getValues("specialDiscountType") || "%";

    let totalSpecialDiscount = 0;
    if (specialDiscountType === "%") {
      totalSpecialDiscount = (subTotal * specialDiscount) / 100;
    } else {
      totalSpecialDiscount = specialDiscount;
    }

    if (subTotal <= 0) return 0;
    return Number.parseFloat(
      ((itemAmount / subTotal) * totalSpecialDiscount).toFixed(2),
    );
  };

  const calculateItemNetAmount = (index) => {
    const baseAmount = calculateItemAmount(index); // without special discount
    const specialDiscountForItem = calculateSpecialDiscountForItem(index);

    let netAmount = baseAmount - specialDiscountForItem;

    // GST apply
    const gst = getValues(`items.${index}.gst`) || 0;
    if (gst > 0) {
      netAmount += (netAmount * gst) / 100;
    }

    return Number.parseFloat(netAmount.toFixed(2));
  };

  useEffect(() => {
    if (data && mode === "edit") {
      try {
        setValue("attachment", data.attachment || "");

        if (data.attachment) {
          const fileName = data.attachment.split("/").pop();
          const fullUrl = `${imageURL}${data.attachment}`;

          setExistingFile({
            name: fileName,
            url: fullUrl,
            path: data.attachment,
          });

          setFile(null);
        }
      } catch (error) {
        console.error("Error parsing data:", error);
      }
    }
  }, [data, mode, setValue]);

  const calculateItemAmount = (index) => {
    const quantity = getValues(`items.${index}.quantity`) || 0;
    const rate = getValues(`items.${index}.rate`) || 0;
    const discount = getValues(`items.${index}.discount`) || 0;
    const discountType =
      getValues(`items.${index}.discountType`) || "Percentage";

    let amount = quantity * rate;

    if (discountType === "Percentage") {
      amount -= (amount * discount) / 100;
    } else {
      amount -= discount;
    }

    return Number.parseFloat(amount.toFixed(2));
  };

  const calculateSubTotal = () => {
    let subTotal = 0;

    fields.forEach((_, index) => {
      subTotal += Number.parseFloat(calculateItemAmount(index)); // Use item-wise calculation
    });

    return subTotal.toFixed(2);
  };

  const calculateDiscountAmount = () => {
    const discount = Number.parseFloat(watch("specialDiscount")) || 0;
    const discountType = watch("specialDiscountType") || "%";
    const subTotal = Number.parseFloat(calculateSubTotal()) || 0;

    let discountAmount = 0;

    if (discountType === "%") {
      discountAmount = (subTotal * discount) / 100;
    } else {
      discountAmount = discount;
    }

    // Ensure discountAmount is always a number
    if (isNaN(discountAmount)) {
      discountAmount = 0;
    }

    return discountAmount.toFixed(2);
  };

  // Add this useEffect to watch for changes in special discount fields
  useEffect(() => {
    // This will force a re-render when special discount or its type changes
  }, [watch("specialDiscount"), watch("specialDiscountType")]);
  const calculateGrandTotal = () => {
    const subTotal = Number.parseFloat(calculateSubTotal());
    const discountAmount = Number.parseFloat(calculateDiscountAmount());
    const shippingCharge = Number.parseFloat(watch("shippingCharge") || 0);
    const groupedGST = calculateGroupedGST();

    const grandTotalBeforeRoundOff =
      subTotal -
      discountAmount +
      shippingCharge +
      Object.values(groupedGST).reduce((sum, val) => sum + val, 0);

    const roundOff =
      Math.round(grandTotalBeforeRoundOff) - grandTotalBeforeRoundOff;
    const grandTotal = grandTotalBeforeRoundOff + roundOff;

    return {
      grandTotal: grandTotal.toFixed(2),
      roundOff: roundOff,
    };
  };

  useEffect(() => {
    setQuotationModule(getModuleSeries(customer_id, "quotation"));
  }, []);

  // Memoize options to prevent unnecessary re-renders
  const contactOptions = useMemo(
    () =>
      contactOption.map((conta) => ({
        value: conta.contactId,
        label: conta.contactName,
        contact_id: conta?.contactId,
        company_id: conta.company_id,
        company_name: conta.company_name,
        billingAddresses: conta.billingAddresses || [],
        shippingAddresses: conta.shippingAddresses || [],
      })),
    [contactOption],
  );

  // Custom option component for better formatting (optional)
  const CustomOption1 = ({
    innerRef,
    innerProps,
    data,
    isSelected,
    isFocused,
  }) => (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        padding: "8px 12px",
        backgroundColor: isSelected
          ? "#007bff"
          : isFocused
            ? "#f8f9fa"
            : "white",
        color: isSelected ? "white" : "#333",
        cursor: "pointer",
      }}
    >
      <div style={{ fontWeight: "500" }}>{data.label}</div>
      <div
        style={{ fontSize: "12px", color: isSelected ? "#e9ecef" : "#6c757d" }}
      >
        {data.company_name || "No Company"}
      </div>
    </div>
  );

  const leadOwnerOptions = useMemo(
    () =>
      leadOwner.map((lead) => ({
        value: lead.leadId,
        label: lead.leadName,
      })),
    [leadOwner],
  );

  const dealOptions = useMemo(
    () =>
      dealOption.map((deal) => ({
        value: deal.dealId,
        label: deal.dealName,
      })),
    [dealOption],
  );

  const productOptions = useMemo(
    () =>
      productOption.map((product) => ({
        value: product.id,
        label: product.service,
        price: product.price,
        tax: product.tax,
      })),
    [productOption],
  );

  const getAvailableOptions = (index) =>
    productOptions?.filter(
      (option) =>
        !items?.some(
          (item, idx) => idx !== index && item.item?.value === option.value,
        ),
    );
  const handleChange = (e) => {
    setFile(e.target.files[0]);
    setValue("attachment", e.target.files[0]);
  };

  const handleClickButton = () => {
    fileInputRef.current.click();
  };

  const handleSelectChange = (name, selectedOption, fieldOnChange) => {
    fieldOnChange(selectedOption?.value || "");

    // Update local state for selected values
    if (name === "contact") {
      setSelectedContact(selectedOption);
      setCompanyId(selectedOption.company_id);
      setContactId(selectedOption.value);
      setSelectedDeal(null);
      setDealId(null);
      setValue("items", [
        {
          item: null,
          quantity: 1,
          rate: 0,
          gst: 0,
          discount: 0,
          discountType: "Percentage",
          description: "",
        },
      ]);
    } else if (name === "leadOwnerId") {
      setSelectedLeadOwner(selectedOption);
    } else if (name === "deal") {
      setSelectedDeal(selectedOption);
      if (selectedOption?.value) {
        setDealId(selectedOption.value);
      }
    }
  };
  const normalizeAddressToArray = (addr, extra = {}) => {
    if (!addr) return [];

    return [
      {
        ...addr,
        ...extra,
      },
    ];
  };

  useEffect(() => {
    if (!data) return;

    try {
      const parsedItems = JSON.parse(data.items || "[]");
      const itemDetails = parsedItems[0]?.itemDetails || [];
      const parseJSON = (value, fallback = []) => {
        try {
          return JSON.parse(value || JSON.stringify(fallback));
        } catch {
          return fallback;
        }
      };
      let billingAddresses = [];
      let shippingAddresses = [];

      if (data?.company_id) {
        const companyAddresses = parseJSON(data.company_billing_address);

        if (companyAddresses.length > 0) {
          billingAddresses = companyAddresses;
          shippingAddresses = companyAddresses;
        } else {
          if (data.billing_address) {
            try {
              billingAddresses = normalizeAddressToArray(
                JSON.parse(data.billing_address),
                { selected: "selected" }
              );
            } catch {
              billingAddresses = [];
            }
          }

          if (data.shipping_address) {
            try {
              shippingAddresses = normalizeAddressToArray(
                JSON.parse(data.shipping_address),
                { for: "shipping" }
              );
            } catch {
              shippingAddresses = [];
            }
          }
        }
      } else {
        const contactAddresses = parseJSON(data.contact_billing_address);

        if (contactAddresses.length > 0) {
          billingAddresses = contactAddresses;
          shippingAddresses = contactAddresses;
        } else {
          if (data.billing_address) {
            try {
              billingAddresses = normalizeAddressToArray(
                JSON.parse(data.billing_address),
                { selected: "selected" }
              );
            } catch {
              billingAddresses = [];
            }
          }

          if (data.shipping_address) {
            try {
              shippingAddresses = normalizeAddressToArray(
                JSON.parse(data.shipping_address),
                { for: "shipping" }
              );
            } catch {
              shippingAddresses = [];
            }
          }
        }
      }
      const dynamicFieldValues = {};
      if (data?.custom_fields?.length) {
        data.custom_fields.forEach((customField) => {
          try {
            dynamicFieldValues[customField.field_id] = JSON.parse(
              customField.value
            );
          } catch {
            dynamicFieldValues[customField.field_id] = customField.value;
          }
        });
        data.custom_fields.forEach((field) => {
          if (dynamicFieldValues[field.id] === undefined) {
            if (
              field.label_type === "date range" ||
              field.label_type === "time-range"
            ) {
              dynamicFieldValues[field.id] = { start: "", end: "" };
            } else if (field.label_type === "multiple-option") {
              dynamicFieldValues[field.id] = [];
            } else {
              dynamicFieldValues[field.id] = "";
            }
          }
        });
      }
      const contactId = data?.contact_id || "";
      const contactName = data?.contact_full_name || "";
      setSelectedContact({
        value: contactId,
        label: contactName,
        customer_id: data.customer_id,
        contact_id: contactId,
        company_id: data.company_id,
        billingAddresses,
        shippingAddresses,
      });
      const formattedItems =
        itemDetails.length > 0
          ? itemDetails.map((item) => ({
            item: {
              value: item.product_id || "",
              label: item.name || "",
            },
            quantity: Number(item.quantity) || 1,
            rate: Number.parseFloat(item.Rate) || 0,
            gst: Number.parseFloat(item.gst) || 0,
            discount: Number.parseFloat(item.discount) || 0,
            discountType: item.discountType || "Percentage",
            description: item.itemDescription || "",
          }))
          : [];
      reset({
        quotationNumber: data.quotation_number || "",
        reference: data.reference_number || "",
        quoteDate: mode === "clone" ? today : data.quotation_date || "",
        expiryDate: mode === "clone" ? tomorrowStr : data.expiry_date || "",
        shortDescription: data.description || "",
        customerNote: data.customer_note || "",
        termsConditions: data.terms_condition || "",
        specialDiscount: parsedItems[0]?.["special-discount"] || 0,
        specialDiscountType:
          parsedItems[0]?.["special_DiscountType"] || "Percentage",
        shippingCharge: parsedItems[0]?.["shipping_Charge"] || 0,
        contact: data.contact_id || "",
        deal: data.deal_id || "",
        leadOwnerId: data.owner_id || "",
        items: formattedItems.length
          ? formattedItems
          : [
            {
              item: "",
              quantity: 1,
              rate: 0,
              gst: 0,
              discount: 0,
              discountType: "Percentage",
              description: "",
            },
          ],
        ...dynamicFieldValues,
      });
    } catch (error) {
      console.error("Error parsing data:", error);
    }
  }, [data, mode, reset]);

  // Old quotations were saved with an empty item description (descriptions were not auto-filled
  // from the product back then). Once both the quotation and the product list are loaded,
  // fill empty item descriptions from the product master - only once per loaded quotation.
  // NOTE: must stay below the effect above, so it runs after reset() has loaded the items.
  const descFilledForRef = useRef(null);
  useEffect(() => {
    if (!data || !productOption?.length) return;
    if (descFilledForRef.current === data) return;

    const currentItems = getValues("items") || [];
    if (!currentItems.some((it) => it?.item?.value)) return; // quotation items not loaded yet

    currentItems.forEach((it, i) => {
      const alreadyHas = it?.description && it.description.trim() !== "";
      if (alreadyHas || !it?.item?.value) return;
      const product = productOption.find(
        (p) => String(p.id) === String(it.item.value),
      );
      if (product?.description) {
        setValue(`items.${i}.description`, product.description);
      }
    });
    descFilledForRef.current = data;
  }, [data, productOption]);

  const handleBillingSubmit = async (data) => {
    const payload = {
      company_id: companyId,
      contact_id: selectedContact?.contact_id,
      billing_address: [
        ...(selectedContact.billingAddresses || []).map((addr) => ({
          address: addr.address,
          country: addr.country,
          state: addr.state,
          city: addr.city,
          zipcode: addr.zipcode,
          selected: "",
          for: addr.for === "shipping" ? "shipping" : "",
        })),
        {
          address: data.address,
          country: data.country || "",
          state: data.state || "",
          city: data.city || "",
          zipcode: data.zipcode,
          selected: "selected",
          for: "",
        },
      ],
    };

    const res = await PutRequest(
      `/quotation/createBillingAddress/`,
      payload,
      { headers },
      extras,
    );

    if (res.status === "success") {
      toast.success(res.message);

      let updatedList = [];
      try {
        updatedList = JSON.parse(res.data.billing_address);
      } catch (err) {
        console.error("Error parsing address list:", err);
      }

      setSelectedContact((prev) => ({
        ...prev,
        billingAddresses: updatedList,
        shippingAddresses: updatedList,
      }));

      setShowBillingModal(false);
    }
  };

  const handleShippingSubmit = async (data) => {
    const payload = {
      company_id: companyId,
      contact_id: selectedContact?.contact_id,
      billing_address: [
        ...(selectedContact.billingAddresses || []).map((addr) => ({
          address: addr.address,
          country: addr.country,
          state: addr.state,
          city: addr.city,
          zipcode: addr.zipcode,
          selected: addr.selected || "",
          for: addr.for === "shipping" ? "" : addr.for || "",
        })),
        {
          address: data.address,
          country: data.country || "",
          state: data.state || "",
          city: data.city || "",
          zipcode: data.zipcode,
          selected: data.selected || "",
          for: "shipping",
        },
      ],
    };

    const res = await PutRequest(
      `/quotation/createBillingAddress/`,
      payload,
      { headers },
      extras,
    );

    if (res.status === "success") {
      toast.success(res.message);

      let updatedList = [];
      try {
        updatedList = JSON.parse(res.data.billing_address);
      } catch (err) {
        console.error("Error parsing address list:", err);
      }

      setSelectedContact((prev) => ({
        ...prev,
        billingAddresses: updatedList,
        shippingAddresses: updatedList,
      }));

      setShowShippingModal(false);
    }
  };

  const handleChangeTextArea = (index) => {
    const description = getValues(`items.${index}.description`);
    if (showTextareaIndex === index) {
      // User is clicking Cancel - clear the field and hide textarea
      setValue(`items.${index}.description`, "");
      setShowTextareaIndex(null);
    } else {
      // User is clicking Add Description - show textarea
      setShowTextareaIndex(index);
    }
  };

  const handleBlur = (index) => {
    const description = getValues(`items.${index}.description`);
    if (description && description.trim() !== "") {
    }
  };

  const handlePClick = (index) => {
    setShowTextareaIndex(index);
  };

  const options = [
    { value: "Percentage", label: "%", icon: "bi bi-percent" },
    { value: "INR", label: "₹", icon: "ti ti-currency-rupee" },
  ];

  const dropdownRefs = useRef([]);

  // Fetch Owners
  const fetchOwners = async () => {
    const res = await GetRequest(
      `users/users/fetchUserAsOwner/${customer_id}`,
      { headers },
      { customer, setCustomer, rf: headers.Authentication, navigate },
    );

    if (res.err) {
      toast.error("Error Fetching Data");
      return;
    }

    const owners = res.data.map((lead) => ({
      leadName: lead.full_name,
      leadId: lead.id,
    }));

    setLeadOwner(owners);

    const currentOwner = getValues("leadOwnerId");
    if (!currentOwner && owners.length > 0) {
      setValue("leadOwnerId", owners[0].leadId, { shouldValidate: true });
      setSelectedLeadOwner({
        value: owners[0].leadId,
        label: owners[0].leadName,
      });
    }
  };

  useEffect(() => {
    fetchOwners();
  }, []);

  const fetchAllContacts = async () => {
    try {
      const res = await GetRequest(
        `quotation/fetchContacts/${loginUserId}/${customer_id}/${userType}`,
        { headers },
        extras,
      );

      if (res.status !== "success") {
        return toast.error(res.message || "Failed to find contacts");
      }

      if (Array.isArray(res.data)) {
        const data = res.data.map((contact) => {
          let billingAddresses = [];
          console.log(contact, "saurabh")
          if (contact.billing_address) {
            try {
              billingAddresses = JSON.parse(contact.billing_address);
            } catch (e) {
              console.error("Billing address parse error:", e);
            }
          }

          if (billingAddresses.length === 0) {
            billingAddresses.push({
              address: contact.address,
              city: contact.city_name,
              state: contact.state_name,
              country: contact.country_name,
              zipcode: contact.zipcode,
              selected: "selected",
              for: "billing",
            });
          }

          const shippingAddresses = [...billingAddresses];

          return {
            contactName: contact.contact_full_name,
            contactId: contact.id,
            company_id: contact.company_id,
            company_name: contact.company_name,
            billingAddresses,
            shippingAddresses,
          };
        });

        setContactOption(data);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Something went wrong while fetching contacts");
    }
  };

  useEffect(() => {
    fetchAllContacts();
  }, []);

  const fetchAllDealById = async () => {
    try {
      const res = await GetRequest(
        `/quotation/fetchContactDeals/${contactId || data.contact_id}`,
        { headers },
        extras,
      );

      if (res.status === "success") {
        const data = res?.data.map((deal) => ({
          dealId: deal.id,
          dealName: deal.deal_name,
        }));

        setDealOptions(data);

        // Important: Always reset selectedDeal and form field if no deals
        if (data.length === 0) {
          setSelectedDeal(null);
          setValue("deal", null); // Important: Use null (not empty string)
          setDealId(null);
        }
      }
    } catch (error) {
      console.error(error);

      // Reset everything on error as well
      setDealOptions([]);
      setSelectedDeal(null);
      setValue("deal", null);
      setDealId(null);
    }
  };

  useEffect(() => {
    fetchAllDealById();
  }, [contactId, data?.contact_id]);

  useEffect(() => {
    if (dealId) {
      fetchProductByDealId();
    }
  }, [dealId, data?.deal_id]);

  const fetchProductByDealId = async () => {
    try {
      const res = await GetRequest(
        `/quotation/fetchDealItems/${dealId || data.deal_id}`,
        { headers },
        extras,
      );

      if (res.status !== "success") {
        return toast.error(res.message || "Failed to fetch products");
      }

      const parsedData = JSON.parse(res.data.product_services).map(
        (product) => ({
          productId: product.product_id,
          productQuantity: product.quantity,
          productAmount: product?.budget || product.amount,
        }),
      );

      setProductToCompair(parsedData);
    } catch (error) {
      console.error("Error fetching product by dealId:", error);
    }
  };

  const fetchAllProductById = async () => {
    try {
      const res = await GetRequest(
        `/masters/getProducts/${customer_id}`,
        { headers },
        extras,
      );

      if (res.status !== "success") {
        return toast.error(res.message);
      }

      // Store the full product data
      setProductOption(res.data);

      // Filter products based on productToCompaire
      const filteredData = res.data.filter((product) =>
        productToCompaire.some((opt) => opt.productId === product.id),
      );

      setTableData(filteredData);

      // Pre-populate form with products from the deal
      if (filteredData.length > 0) {
        const initialItems = filteredData.map((product) => {
          const compaireItem = productToCompaire.find(
            (opt) => opt.productId === product.id,
          );
          return {
            item: { value: product.id, label: product.service },
            quantity: compaireItem?.productQuantity || 1,
            rate: compaireItem?.productAmount || product.price || 0,
            gst: product.tax || 0,
            discount: 0,
            discountType: "Percentage",
            description: product.description || "",
          };
        });

        // Reset only the items field, preserving other form values
        setValue("items", initialItems);
      }
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    fetchAllProductById();
  }, [productToCompaire, customer_id]);

  const addRow = () => {
    append({
      item: null,
      quantity: 1,
      rate: 0,
      gst: 0,
      discount: 0,
      discountType: "Percentage",
      description: "",
    });
  };

  const handleDeleteRow = (index) => {
    remove(index);
  };

  const handleSelect = (index, value) => {
    setValue(`items.${index}.discountType`, value);
  };
const noFillRowStyle = {
  background: "#fff",
  backgroundColor: "#fff",
  boxShadow: "none",
  "--bs-table-bg": "#fff",
  "--bs-table-accent-bg": "transparent",
};
  const handleProductSelect = (selected, index, fieldOnChange) => {
    fieldOnChange(selected);

    if (selected) {
      const product = productOption.find((p) => p.id === selected.value);
      if (product) {
        setValue(`items.${index}.rate`, product.price || 0, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        setValue(`items.${index}.gst`, product.tax || 0, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });

        setValue(`items.${index}.quantity`, 1, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });

        setValue(`items.${index}.description`, product.description || "", { shouldDirty: true });
      }
    } else {
      setValue(`items.${index}.rate`, 0, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(`items.${index}.gst`, 0, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });

      setValue(`items.${index}.quantity`, 1, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
       setValue(`items.${index}.description`, "", { shouldDirty: true });
    }
  };

  const customStyle = {
    control: (provided) => ({
      ...provided,
      padding: "0rem 0.5rem 0rem",
      minHeight: "35px",
      maxHeight: 38,
      backgroundPosition: "right 0.4rem center",
      border: "1px solid #ccc",
      boxShadow: "none",
    }),
    placeholder: (provided) => ({
      ...provided,
      margin: "0rem 0px 0.2rem",
      fontSize: "0.9rem",
    }),
  };

  const calculateGstAmount = () => {
    const gstPercentage = getValues("gstPercentage") || 0;
    const subTotal = Number.parseFloat(calculateSubTotal());
    const discountAmount = Number.parseFloat(calculateDiscountAmount());
    const amountAfterDiscount = subTotal - discountAmount;
    const gstAmount = (amountAfterDiscount * gstPercentage) / 100;

    return gstAmount.toFixed(2);
  };

  const quotation_id = data?.quotation_id;
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const { grandTotal, roundOff } = calculateGrandTotal();

      const selectedBilling =
        selectedContact?.billingAddresses?.find(
          (addr) => addr.selected === "selected",
        ) || null;

      // const selectedShipping =
      //   selectedContact?.shippingAddresses?.find(
      //     (addr) => addr.selected === "selected"
      //   ) || null;
      const selectedShipping =
        selectedContact?.shippingAddresses?.find(
          (addr) => addr.for === "shipping",
        ) ||
        selectedContact?.shippingAddresses?.find(
          (addr) => addr.selected === "selected",
        ) ||
        selectedContact?.shippingAddresses?.[0];

      let attachmentToSend;

      if (file) {
        attachmentToSend = file;
      } else if (existingFile) {
        attachmentToSend = existingFile.path;
      }

      let status;
      if (draftClick) {
        status = "draft";
      } else {
        status = "pending";
      }

      const groupedGstMap = calculateGroupedGST();

      const gstBreakdown = Object.entries(groupedGstMap).map(
        ([gst, amount]) => ({
          gst,
          amount,
        }),
      );

      const custom_fields = [];
      const staticFieldsPayload = { ...data };
      // dynamicFields.forEach((field) => {
      //   const fieldName = field.id
      //   if (fieldName && data.hasOwnProperty(fieldName)) {
      //     let formattedValue = data[fieldName]
      //     let isRangeField = false // Initialize flag for range property

      //     // Handle complex types for custom_fields payload
      //     if (
      //       field.label_type === "date range" ||
      //       field.label_type === "time-range"
      //     ) {
      //       isRangeField = true // Set flag for range fields
      //       // Ensure both start and end are present before sending
      //       if (
      //         !formattedValue ||
      //         (!formattedValue.start && !formattedValue.end)
      //       ) {
      //         formattedValue = { start: "", end: "" } // Send empty object if range is incomplete/empty
      //       }
      //     } else if (field.label_type === "multiple-option") {
      //       // Ensure it's an array
      //       if (!Array.isArray(formattedValue)) {
      //         formattedValue = [] // Send empty array if not an array
      //       }
      //     }
      //     const customFieldEntry = {
      //       field_id: field.id.toString(), // Ensure ID is a string
      //       value: formattedValue,
      //       range: isRangeField ? true : false,
      //     }
      //     custom_fields.push(customFieldEntry)
      //     // Remove dynamic field from static payload to avoid duplication
      //     delete staticFieldsPayload[fieldName]
      //   }
      // })
      dynamicFields.forEach((field) => {
        const fieldName = field.id;

        if (fieldName && data.hasOwnProperty(fieldName)) {
          let formattedValue = data[fieldName];
          let isRangeField = false;
          // Handle range fields
          if (
            field.label_type === "date range" ||
            field.label_type === "time-range"
          ) {
            isRangeField = true;
            if (
              !formattedValue ||
              (!formattedValue.start && !formattedValue.end)
            ) {
              formattedValue = null;
            }
          }
          // Handle multiple option
          else if (field.label_type === "multiple-option") {
            if (!Array.isArray(formattedValue) || formattedValue.length === 0) {
              formattedValue = null;
            }
          }
          if (
            formattedValue === undefined ||
            formattedValue === null ||
            formattedValue === ""
          ) {
            delete staticFieldsPayload[fieldName];
            return;
          }
          const customFieldEntry = {
            field_id: field.id.toString(),
            value: formattedValue,
            range: isRangeField,
          };
          custom_fields.push(customFieldEntry);
          delete staticFieldsPayload[fieldName];
        }
      });

      const dataToSend = {
        quotation_id: quotation_id,
        contact_id: selectedContact?.contactId || data.contact,
        quotation_number: displayQuotationNumber,
        reference_number: data.reference || "",
        quotation_date: data.quoteDate,
        expiry_date: data.expiryDate,
        owner_id: data.leadOwnerId,
        deal_id: data.deal,
        description: data.shortDescription,
        quotation_status: status,

        items: [
          {
            itemDetails: data.items.map((val, i) => {
              const quantity = val.quantity || 0;
              const rate = val.rate || 0;
              const discount = val.discount || 0;
              const discountType = val.discountType || "Percentage";
              const discription = val.description || "";
              // Calculate discounted amount
              let discountedAmount = quantity * rate;
              if (discountType === "Percentage") {
                discountedAmount -= (discountedAmount * discount) / 100;
              } else {
                discountedAmount -= discount;
              }

              return {
                product_id: val.item.value,
                name: val.item.label,
                quantity: quantity,
                itemDescription: discription,
                Rate: rate,
                gst: val.gst,
                discount: discount,
                discountType: discountType,
                amount: quantity * rate,
                discountedAmount: Number.parseFloat(
                  discountedAmount.toFixed(2),
                ),
              };
            }),
            "sub-total": calculateSubTotal(),
            "special-discount": data.specialDiscount,
            gstBreakdown,
            "grand-total": grandTotal,
            special_DiscountType: data.specialDiscountType || "INR",
            special_Discount_Amount: calculateDiscountAmount(),
            shipping_Charge: data.shippingCharge,
          },
        ],

        customer_note: data.customerNote,
        customer_id: customer_id,
        terms_condition: data.termsConditions,
        attachment: attachmentToSend || "",

        user_login_id: loginUserId,

        billing_address: selectedBilling,
        shipping_address: selectedShipping,
      };

      const finalPayload = {
        ...dataToSend,
        custom_fields,
      };

      let res;

      if (mode === "clone") {
        if (!quotationGrant?.isRevised) {
          toast.warning("You are not authorized to revised this quotation!");
          return navigate(-1);
        }
        // For clone, use POST request to create new quotation
        res = await PostRequest(
          `/quotation/revisedQuotation`,
          finalPayload,
          true,
          { headers },
          extras,
        );
      }else if(mode === "cloneRe"){
           res = await PostRequest(
          `/quotation/revisedQuotation`,
          finalPayload,
          true,
          { headers },
          extras,
        );
      } else {
        res = await PostRequest(
          `/quotation/updateQuotation`,
          finalPayload,
          true,
          { headers },
          extras,
        );
      }

      if (res.status !== "success" || res.status === "fail") {
        toast.error(res.message);
      } else {
        toast.success(
          `Quotation ${mode === "clone" ? "revised" : mode === "cloneRe" ? "cloned" : "updated"} successfully!`,
        );

        setTimeout(() => {
          if (onClose) onClose();
          if (From === "detailPage") {
            navigate(`/detailquotation/${paramId}`);
          } else {
            navigate("/quotations");
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        `Failed to ${mode === "clone" ? "clone" : "update"} quotation`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setDraftClicked(false);
    setTimeout(() => {
      if (onClose) onClose();
      if (From === "detailPage") {
        navigate(`/detailquotation/${paramId}`);
      } else {
        navigate("/quotations");
      }
    }, 100);
  };

  //use effect for dynamic fields
  useEffect(() => {
    (async () => {
      try {
        const res = await GetRequest(
          `/common/fetchCustomFields/${customer_id}/quotation`,
          { headers },
          extras,
        );

        if (res.success === false || res.success === "fail") {
          toast.error(res.message);
          return;
        }
        res.data?.grouped != {} &&
          setGrouped(res.data?.grouped && res.data?.grouped);
        res.data?.ungrouped != {} &&
          setUnGrouped(res.data?.ungrouped && res.data?.ungrouped);
        // Update dynamic fields state to trigger schema recalculation
        const allFields = [
          ...(res.data?.grouped?.flatMap((g) => g.groupFields || []) || []),
          ...(res.data?.ungrouped || []),
        ];
        setDynamicFields(allFields);
      } catch (error) {
        toast.error("Internal Server Error");
        return;
      }
    })();
  }, [customer]);

  const { roundOff, grandTotal } = calculateGrandTotal();

  return (
    <>
      {!isQuotation && <UpgradePlan moduleName="Quotation" />}
      {!quotationGrant?.isEdit ? (
        <AuthorizationCard />
      ) : (
        <>
          {loading && 
           <Spinner text="Processing..." />
          }
          <div
            style={{
              borderRadius: 7,
              background: "#fff",
              height: "calc(100vh - 12vh)",
              border: "1px solid lightblue",
              overflow: "clip",
            }}
          >
            <div
              className="header mb-0 position-sticky top-0 p-3 "
              style={{
                borderBottom: "1px solid lightblue",
                zIndex: 11,
                background: "#fff",
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex ">
                  <h3
                    className=" mb-0"
                    style={{
                      whiteSpace: "nowrap",
                      fontWeight: 300,
                      fontSize: "22px",
                    }}
                  >
                    {mode === "clone" ? "Revised Quotation" : mode === "cloneRe" ? "Clone Quotation" : "Edit Quotation"}
                  </h3>

                  <p
                    className="mt-1 mx-3 text-success fw-semibold"
                    style={{ fontWeight: "14px" }}
                  >
                    (#{displayQuotationNumber})
                  </p>
                </div>
                <button
                  className="btn btn-default-outline"
                  aria-label="Go Back"
                  type="button"
                  onClick={handleCancel}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>

            <Form
              onSubmit={handleSubmit(onSubmit)}
              className="form-container"
              encType="multipart/form-data"
              style={{
                margin: 0,
                overflowY: "auto",
                height: "100%",
                paddingLeft: "0px",
                paddingTop: "12px",
                position: "relative",
              }}
            >
              <Row className="ms-0 w-100 mb-0">
                <Col md={2} style={{ borderRight: "1px solid #d4e7ed" }}>
                  <div
                    style={{ position: "sticky", top: "1rem" }}
                    className=" mt-3"
                  >
                    <ul className=" ps-3" style={{ listStyle: "none" }}>
                      <li className="position-relative border-start ps-3 py-2 pt-0 ps-4">
                        <span
                          className="position-absolute  start-0 translate-middle bg-primary rounded-circle"
                          style={{
                            width: 10,
                            height: 10,
                            top: 11,
                            border: "1px solid #0099ff",
                          }}
                        ></span>
                        <a
                          href="#companyDetailsSection"
                          style={{ color: "#676262" }}
                        >
                          {" "}
                          Company Quotation{" "}
                        </a>
                      </li>

                      <li className="position-relative border-start ps-3 py-2 ps-4">
                        <span
                          className="position-absolute top-50 start-0 translate-middle bg-primary rounded-circle"
                          style={{ width: 10, height: 10 }}
                        ></span>
                        <a
                          href="#itemsDetailsSection"
                          style={{ color: "#676262" }}
                        >
                          {" "}
                          Items Details{" "}
                        </a>
                      </li>
                      <li className="position-relative border-start ps-3 py-2 ps-4">
                        <span
                          className="position-absolute top-50 start-0 translate-middle bg-primary rounded-circle"
                          style={{ width: 10, height: 10 }}
                        ></span>
                        <a
                          href="#termsAndConditionsSec"
                          style={{ color: "#676262" }}
                        >
                          {" "}
                          Terms & Conditions{" "}
                        </a>
                      </li>
                    </ul>
                    <hr />
                  </div>
                </Col>

                <Col md={10} className="ps-3">
                  <Row className="ms-0 w-100" id="companyDetailsSection">
                    <h3
                      className=" mb-4 pt-3"
                      style={{
                        whiteSpace: "nowrap",
                        fontWeight: 300,
                        fontSize: "19px",
                      }}
                    >
                      {" "}
                      Company Quotation
                    </h3>

                    <Row className="ms-0 w-100">
                      <Col md={6} className="ps-0">
                        <Col md={12}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formContact"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              Contact <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                              name="contact"
                              control={control}
                              rules={{ required: "Contact is required" }}
                              render={({ field }) => {
                                return (
                                  <Select
                                    {...field}
                                    options={contactOptions}
                                    placeholder="Select Contact"
                                    classNamePrefix="react-select"
                                    styles={customStyles}
                                    isDisabled={mode === "edit"}
                                    // Use custom component to show company name below contact name
                                    components={{
                                      Option: CustomOption1,
                                    }}
                                    value={
                                      contactOptions?.find(
                                        (opt) => opt.value === field.value,
                                      ) || selectedContact
                                    }
                                    onChange={(selected) =>
                                      handleSelectChange(
                                        "contact",
                                        selected,
                                        field.onChange,
                                      )
                                    }
                                  />
                                );
                              }}
                            />

                            {errors.contact && (
                              <p
                                className="text-danger mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                {errors.contact.message}
                              </p>
                            )}
                          </Form.Group>
                        </Col>

                        {mode === "clone" ? (
                          <Row>
                            <Col md={12}>
                              <Form.Group
                                className="mb-3 focus-z-index position-relative"
                                controlId="formQuotationNumber"
                              >
                                <FontAwesomeIcon
                                  icon={faGear}
                                  style={{
                                    position: "absolute",
                                    marginTop: 18,
                                    right: 15,
                                    color: "grey",
                                    cursor: "pointer",
                                    zIndex: 1,
                                  }}
                                  onClick={handleShow}
                                />
                                <Form.Label style={{ marginBottom: 1 }}>
                                  Revision Number{" "}
                                  <span className="text-danger">*</span>
                                </Form.Label>
                                <Controller
                                  name="quotationNumbe"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <Form.Control
                                      {...field}
                                      type="text"
                                      placeholder="Enter Revision Number"
                                      // yaha input change hote hi revision update
                                      onChange={(e) => {
                                        field.onChange(e);
                                        setRevision(e.target.value);
                                      }}
                                    />
                                  )}
                                />
                                {errors.quotationNumber && (
                                  <p
                                    className="text-danger mt-1"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {errors.quotationNumber.message}
                                  </p>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        ) : null}
                        <Row>
                          <Col md={12}>
                            <Form.Group
                              className="mb-3 focus-z-index position-relative"
                              controlId="formReference"
                            >
                              <Form.Label style={{ marginBottom: 1 }}>
                                Reference
                              </Form.Label>
                              <Controller
                                name="reference"
                                control={control}
                                render={({ field }) => (
                                  <Form.Control
                                    {...field}
                                    type="text"
                                    placeholder="Enter Reference"
                                  />
                                )}
                              />
                              <img
                                src={infoImg || "/placeholder.svg"}
                                alt=""
                                style={{
                                  width: 25,
                                  height: 25,
                                  position: "absolute",
                                  right: 10,
                                  top: 13,
                                }}
                              />
                              {errors.reference && (
                                <p
                                  className="text-danger mt-1"
                                  style={{ fontSize: "12px" }}
                                >
                                  {errors.reference.message}
                                </p>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        {/* <Row>
                      <Col md={6}>
                        <Form.Group
                          className="mb-3 focus-z-index position-relative"
                          controlId="formQuoteDate"
                        >
                          <Form.Label style={{ marginBottom: 1 }}>
                            Quote Date <span className="text-danger">*</span>
                          </Form.Label>
                          <Controller
                            name="quoteDate"
                            control={control}
                            rules={{ required: "Quote Date is required" }}
                            render={({ field }) => (
                              <Form.Control
                                {...field}
                                type="date"
                                placeholder="Select Quote Date"
                              />
                            )}
                          />
                          {errors.quoteDate && (
                            <p
                              className="text-danger mt-1"
                              style={{ fontSize: "12px" }}
                            >
                              {errors.quoteDate.message}
                            </p>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group
                          className="mb-3 focus-z-index position-relative"
                          controlId="formExpiryDate"
                        >
                          <Form.Label style={{ marginBottom: 1 }}>
                            Expiry Date <span className="text-danger">*</span>
                          </Form.Label>

                          <Controller
                            name="expiryDate"
                            control={control}
                            rules={{
                              required: "Expiry Date is required",
                              validate: (value) => {
                                if (!value) return "Expiry Date is required";

                                if (quoteDate) {
                                  const qDate = new Date(quoteDate);
                                  const eDate = new Date(value);
                                  if (eDate < qDate) {
                                    return "Expiry Date cannot be before Quote Date";
                                  }
                                }
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <Form.Control
                                {...field}
                                type="date"
                                placeholder="Select Expiry Date"
                                min={
                                  quoteDate ||
                                  new Date().toISOString().split("T")[0]
                                }
                              />
                            )}
                          />

                          {errors.expiryDate && (
                            <p
                              className="text-danger mt-1"
                              style={{ fontSize: "12px" }}
                            >
                              {errors.expiryDate.message}
                            </p>
                          )}
                        </Form.Group>
                      </Col>
                    </Row> */}

                        <Row>
                          <Col md={6}>
                            <Form.Group
                              className="mb-3 focus-z-index position-relative"
                              controlId="formQuoteDate"
                            >
                              <Form.Label style={{ marginBottom: 1 }}>
                                Quote Date{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>

                              <Controller
                                name="quoteDate"
                                control={control}
                                rules={{
                                  required: "Quote Date is required",
                                  validate: (value) => {
                                    const qDate = new Date(value);
                                    const tDate = new Date(today);

                                    if (qDate < tDate) {
                                      return "Quote Date cannot be in the past";
                                    }
                                    return true;
                                  },
                                }}
                                render={({ field }) => (
                                  <Form.Control
                                    {...field}
                                    type="date"
                                    placeholder="Select Quote Date"
                                    min={mode === "edit" ? "" : today}
                                  />
                                )}
                              />

                              {errors.quoteDate && (
                                <p
                                  className="text-danger mt-1"
                                  style={{ fontSize: "12px" }}
                                >
                                  {errors.quoteDate.message}
                                </p>
                              )}
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group
                              className="mb-3 focus-z-index position-relative"
                              controlId="formExpiryDate"
                            >
                              <Form.Label style={{ marginBottom: 1 }}>
                                Expiry Date{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>

                              <Controller
                                name="expiryDate"
                                control={control}
                                rules={{
                                  required: "Expiry Date is required",
                                  validate: (value) => {
                                    const qDate = new Date(quoteDate);
                                    const eDate = new Date(value);
                                    if (eDate <= qDate) {
                                      return "Expiry Date must be after Quote Date";
                                    }
                                    return true;
                                  },
                                }}
                                render={({ field }) => (
                                  <Form.Control
                                    {...field}
                                    type="date"
                                    placeholder="Select Expiry Date"
                                    min={quoteDate}
                                  />
                                )}
                              />

                              {eDate < qDate && (
                                <p
                                  className="text-danger mt-1"
                                  style={{ fontSize: "12px" }}
                                >
                                  Expiry Date must be after Quote Date
                                </p>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <Form.Group
                              className="mb-3 focus-z-index position-relative"
                              controlId="formOwner"
                            >
                              <Form.Label style={{ marginBottom: 1 }}>
                                Company Account Owner
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Controller
                                name="leadOwnerId"
                                control={control}
                                rules={{ required: "Owner is required" }}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    isDisabled={!isOwnerSame}
                                    options={leadOwnerOptions}
                                    placeholder="Select Owner"
                                    classNamePrefix="react-select"
                                    styles={customStyles}
                                    value={
                                      leadOwnerOptions.find(
                                        (opt) => opt.value === field.value,
                                      ) || selectedLeadOwner
                                    }
                                    onChange={(selected) =>
                                      handleSelectChange(
                                        "leadOwnerId",
                                        selected,
                                        field.onChange,
                                      )
                                    }
                                  />
                                )}
                              />

                              {errors.leadOwnerId && (
                                <p
                                  className="text-danger mt-1"
                                  style={{ fontSize: "12px" }}
                                >
                                  {errors.leadOwnerId.message}
                                </p>
                              )}
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group
                              className="mb-3 focus-z-index position-relative"
                              controlId="formDeal"
                            >
                              <Form.Label style={{ marginBottom: 1 }}>
                                Deal <span className="text-danger">*</span>
                              </Form.Label>
                              <Controller
                                name="deal"
                                control={control}
                                rules={{ required: "Deal is required" }}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    options={dealOptions}
                                    placeholder="Select Deal"
                                    classNamePrefix="react-select"
                                    styles={customStyles}
                                    value={
                                      dealOptions.find(
                                        (opt) => opt.value === field.value,
                                      ) || selectedDeal
                                    }
                                    onChange={(selected) =>
                                      handleSelectChange(
                                        "deal",
                                        selected,
                                        field.onChange,
                                      )
                                    }
                                  />
                                )}
                              />

                              {errors.deal && (
                                <p
                                  className="text-danger mt-1"
                                  style={{ fontSize: "12px" }}
                                >
                                  {errors.deal.message}
                                </p>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={12}>
                            <Form.Group
                              className="mb-3 focus-z-index position-relative"
                              controlId="formShortDescription"
                            >
                              <Form.Label
                                style={{ marginBottom: 1 }}
                                className="textareaLables"
                              >
                                Short Description
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Controller
                                name="shortDescription"
                                control={control}
                                render={({ field }) => (
                                  <textarea
                                    {...field}
                                    className="form-control"
                                    placeholder="Enter Short Description"
                                    rows={3}
                                  />
                                )}
                              />
                              {errors.shortDescription && (
                                <p
                                  className="text-danger mt-1"
                                  style={{ fontSize: "12px" }}
                                >
                                  {errors.shortDescription.message}
                                </p>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                      </Col>
                      <Col
                        md={5}
                        className="mb-3 ms-3 ps-4"
                        style={{ borderLeft: "1px solid #dae8ed" }}
                      >
                        {selectedContact && (
                          <div className="mt-0 mb-2 col-md-12">
                            {/* Billing Address Section */}
                            <div className="w-100 mb-4">
                              <p
                                className="mb-2 fs-13"
                                style={{
                                  color: "#4c526c",
                                  textTransform: "uppercase",
                                }}
                              >
                                Billing Address{" "}
                                <FontAwesomeIcon
                                  icon={faPen}
                                  style={{
                                    color: "#b7a3a3",
                                    cursor: "pointer",
                                  }}
                                  className="ms-1"
                                  onClick={() => setShowBillingModal(true)}
                                />
                              </p>

                              {selectedContact?.billingAddresses?.filter(
                                (addr) => addr?.selected === "selected",
                              ).length > 0 ? (
                                selectedContact?.billingAddresses
                                  .filter(
                                    (addr) => addr?.selected === "selected",
                                  )
                                  .map((addr, i) => (
                                    <div
                                      key={i}
                                      className="mb-3 p-2 rounded border border-primary"
                                      style={{ minHeight: "80px" }}
                                    >
                                      <p
                                        className="mb-0 fs-13 text-dark"
                                        style={{ fontWeight: 400 }}
                                      >
                                        {addr?.address}
                                      </p>

                                      <p
                                        className="mb-0 fs-13 text-dark"
                                        style={{ fontWeight: 400 }}
                                      >
                                        {typeof addr?.city === "object"
                                          ? addr?.city?.label ||
                                          addr?.city?.value
                                          : addr?.city}
                                        ,{" "}
                                        {typeof addr?.state === "object"
                                          ? addr?.state?.label ||
                                          addr?.state?.value
                                          : addr?.state}
                                        ,{" "}
                                        {typeof addr?.country === "object"
                                          ? addr?.country?.label ||
                                          addr?.country?.value
                                          : addr?.country}{" "}
                                        - {addr?.zipcode}
                                      </p>
                                    </div>
                                  ))
                              ) : (
                                <p className="text-muted fs-13">
                                  No billing address available
                                </p>
                              )}
                            </div>

                            <div className="w-100">
                              <p
                                className="mb-2 fs-13"
                                style={{
                                  color: "#4c526c",
                                  textTransform: "uppercase",
                                }}
                              >
                                Shipping Address{" "}
                                <FontAwesomeIcon
                                  icon={faPen}
                                  style={{
                                    color: "#b7a3a3",
                                    cursor: "pointer",
                                  }}
                                  className="ms-1"
                                  onClick={() => setShowShippingModal(true)}
                                />
                              </p>

                              {selectedContact.shippingAddresses?.filter(
                                (addr) => addr.for === "shipping",
                              ).length > 0 ? (
                                selectedContact.shippingAddresses
                                  .filter((addr) => addr.for === "shipping")
                                  .map((addr, i) => (
                                    <div
                                      key={i}
                                      className="mb-3 p-2 rounded border border-primary"
                                      style={{ minHeight: "80px" }}
                                    >
                                      <p
                                        className="mb-0 fs-13 text-dark"
                                        style={{ fontWeight: 400 }}
                                      >
                                        {addr.address}
                                      </p>

                                      <p
                                        className="mb-0 fs-13 text-dark"
                                        style={{ fontWeight: 400 }}
                                      >
                                        {typeof addr?.city === "object"
                                          ? addr?.city?.label ||
                                          addr?.city?.value
                                          : addr?.city}
                                        ,{" "}
                                        {typeof addr?.state === "object"
                                          ? addr?.state?.label ||
                                          addr?.state?.value
                                          : addr?.state}
                                        ,{" "}
                                        {typeof addr?.country === "object"
                                          ? addr?.country?.label ||
                                          addr?.country?.value
                                          : addr?.country}{" "}
                                        - {addr?.zipcode}
                                      </p>
                                    </div>
                                  ))
                              ) : selectedContact.billingAddresses?.filter(
                                (addr) => addr.selected === "selected",
                              ).length > 0 ? (
                                selectedContact.billingAddresses
                                  .filter(
                                    (addr) => addr.selected === "selected",
                                  )
                                  .map((addr, i) => (
                                    <div
                                      key={i}
                                      className="mb-3 p-2 rounded border border-primary"
                                    >
                                      <p
                                        className="mb-0 fs-13 text-dark"
                                        style={{ fontWeight: 400 }}
                                      >
                                        {addr.address}
                                      </p>

                                      <p
                                        className="mb-0 fs-13 text-dark"
                                        style={{ fontWeight: 400 }}
                                      >
                                        {typeof addr?.city === "object"
                                          ? addr?.city?.label ||
                                          addr?.city?.value
                                          : addr?.city}
                                        ,{" "}
                                        {typeof addr?.state === "object"
                                          ? addr?.state?.label ||
                                          addr?.state?.value
                                          : addr?.state}
                                        ,{" "}
                                        {typeof addr?.country === "object"
                                          ? addr?.country?.label ||
                                          addr?.country?.value
                                          : addr?.country}{" "}
                                        - {addr?.zipcode}
                                      </p>
                                    </div>
                                  ))
                              ) : (
                                <p className="text-muted fs-13">
                                  No shipping address available
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        {/* {selectedContact && (
                        <div className="mt-0 mb-2 col-md-12">
                          <div className="w-100 mb-4">
                            <p
                              className="mb-2 fs-13"
                              style={{
                                color: "#4c526c",
                                textTransform: "uppercase",
                              }}
                            >
                              Billing Address{" "}
                              <FontAwesomeIcon
                                icon={faPen}
                                style={{ color: "#b7a3a3", cursor: "pointer" }}
                                className="ms-1"
                                onClick={() => setShowBillingModal(true)}
                              />
                            </p>

                            {selectedContact.billingAddresses?.filter(
                              (addr) => addr.selected === "selected"
                            ).length > 0 ? (
                              selectedContact.billingAddresses
                                .filter((addr) => addr.selected === "selected")
                                .map((addr, i) => (
                                  <div
                                    key={i}
                                    className="mb-3 p-2 rounded border border-primary"
                                    style={{ minHeight: "80px" }}
                                  >
                                    <p
                                      className="mb-0 fs-13 text-dark"
                                      style={{ fontWeight: 400 }}
                                    >
                                      {addr.address}
                                    </p>
                                    <p
                                      className="mb-0 fs-13 text-dark"
                                      style={{ fontWeight: 400 }}
                                    >
                                      {addr.city}, {addr.state}, {addr.country} -{" "}
                                      {addr.zipcode}
                                    </p>
                                  </div>
                                ))
                            ) : (
                              <p className="text-muted fs-13">
                                No billing address available
                              </p>
                            )}
                          </div>

                          <div className="w-100">
                            <p
                              className="mb-2 fs-13"
                              style={{
                                color: "#4c526c",
                                textTransform: "uppercase",
                              }}
                            >
                              Shipping Address{" "}
                              <FontAwesomeIcon
                                icon={faPen}
                                style={{ color: "#b7a3a3", cursor: "pointer" }}
                                className="ms-1"
                                onClick={() => setShowShippingModal(true)}
                              />
                            </p>

                            {selectedContact.shippingAddresses?.filter(
                              (addr) => addr.for === "shipping"
                            ).length > 0 ? (
                              selectedContact.shippingAddresses
                                .filter((addr) => addr.for === "shipping")
                                .map((addr, i) => (
                                  <div
                                    key={i}
                                    className="mb-3 p-2 rounded border border-primary"
                                    style={{ minHeight: "80px" }}
                                  >
                                    <p
                                      className="mb-0 fs-13 text-dark"
                                      style={{ fontWeight: 400 }}
                                    >
                                      {addr.address}
                                    </p>
                                    <p
                                      className="mb-0 fs-13 text-dark"
                                      style={{ fontWeight: 400 }}
                                    >
                                      {addr.city}, {addr.state}, {addr.country} -{" "}
                                      {addr.zipcode}
                                    </p>
                                  </div>
                                ))
                            ) : selectedContact.billingAddresses?.filter(
                              (addr) => addr.selected === "selected"
                            ).length > 0 ? (
                              selectedContact.billingAddresses
                                .filter((addr) => addr.selected === "selected")
                                .map((addr, i) => (
                                  <div
                                    key={i}
                                    className="mb-3 p-2 rounded border border-primary"
                                  >
                                    <p
                                      className="mb-0 fs-13 text-dark"
                                      style={{ fontWeight: 400 }}
                                    >
                                      {addr.address}
                                    </p>
                                    <p
                                      className="mb-0 fs-13 text-dark"
                                      style={{ fontWeight: 400 }}
                                    >
                                      {addr.city}, {addr.state}, {addr.country} -{" "}
                                      {addr.zipcode}
                                    </p>
                                  </div>
                                ))
                            ) : (
                              <p className="text-muted fs-13">
                                No shipping address available
                              </p>
                            )}
                          </div>
                        </div>
                      )} */}
                      </Col>
                    </Row>
                    {/* <hr /> */}
                    {/* grouped custom fields */}
                    <div style={{ marginBottom: "80px", marginLeft: "-32px" }}>
                      {grouped &&
                        grouped.length !== 0 &&
                        grouped.map((g) => {
                          const capitalizedName = g.group_name.replace(
                            /\b\w/g,
                            (char) => char.toUpperCase(),
                          );
                          return (
                            <React.Fragment
                              key={g.id}
                              style={{ marginBottom: "100px" }}
                            >
                              {g.groupFields?.length !== 0 && <hr />}
                              <div className="offcanvasSections">
                                <h4
                                  id={capitalizedName}
                                  className="offcanvasSecHeading"
                                >
                                  {g.groupFields?.length !== 0 &&
                                    capitalizedName}
                                </h4>
                                <Row className="w-100 ms-0 pt-3">
                                  {g.groupFields?.length !== 0 &&
                                    g.groupFields?.map((field) => {
                                      return (
                                        <React.Fragment key={field.id}>
                                          {field.label_type === "text" && (
                                            <Col md={6}>
                                              <Text
                                                name={field.id}
                                                label_name={field.label_name}
                                                label_type={field.label_type}
                                                control={control}
                                                rules={{
                                                  required: `${field.label_name} is required`,
                                                }}
                                                isRequired={
                                                  field.is_required === "yes"
                                                }
                                                isImportant={
                                                  field.is_important === "yes"
                                                }
                                              />
                                            </Col>
                                          )}
                                          {field.label_type === "monetary" && (
                                            <Col md={6}>
                                              <Text
                                                name={field.id}
                                                label_name={field.label_name}
                                                label_type={field.label_type}
                                                control={control}
                                                rules={{
                                                  required: `${field.label_name} is required`,
                                                }}
                                                isRequired={
                                                  field.is_required === "yes"
                                                }
                                                isImportant={
                                                  field.is_important === "yes"
                                                }
                                              />
                                            </Col>
                                          )}
                                          {field.label_type === "largeText" && (
                                            <Col md={6}>
                                              <Text
                                                name={field.id}
                                                label_name={field.label_name}
                                                label_type={field.label_type}
                                                control={control}
                                                rules={{
                                                  required: `${field.label_name} is required`,
                                                }}
                                                isRequired={
                                                  field.is_required === "yes"
                                                }
                                                isImportant={
                                                  field.is_important === "yes"
                                                }
                                              />
                                            </Col>
                                          )}
                                          {field.label_type === "numerical" && (
                                            <Col md={6}>
                                              <Text
                                                name={field.id}
                                                label_name={field.label_name}
                                                label_type={field.label_type}
                                                control={control}
                                                rules={{
                                                  required: `${field.label_name} is required`,
                                                }}
                                                isRequired={
                                                  field.is_required === "yes"
                                                }
                                                isImportant={
                                                  field.is_important === "yes"
                                                }
                                              />
                                            </Col>
                                          )}
                                          {field.label_type === "address" && (
                                            <Col md={6}>
                                              <Text
                                                name={field.id}
                                                label_name={field.label_name}
                                                label_type={field.label_type}
                                                control={control}
                                                rules={{
                                                  required: `${field.label_name} is required`,
                                                }}
                                                isRequired={
                                                  field.is_required === "yes"
                                                }
                                                isImportant={
                                                  field.is_important === "yes"
                                                }
                                              />
                                            </Col>
                                          )}
                                          {field.label_type ===
                                            "single-option" && (
                                              <Col md={6}>
                                                <SelectOpt
                                                  name={field.id}
                                                  label_name={field.label_name}
                                                  label_type={field.label_type}
                                                  control={control}
                                                  rules={{
                                                    required: `${field.label_name} is required`,
                                                  }}
                                                  isRequired={
                                                    field.is_required === "yes"
                                                  }
                                                  isImportant={
                                                    field.is_important === "yes"
                                                  }
                                                  options={
                                                    field.option !== null &&
                                                    field.option
                                                  }
                                                />
                                              </Col>
                                            )}
                                          {field.label_type === "user" && (
                                            <Col md={6}>
                                              <SelectOpt
                                                name={field.id}
                                                label_name={field.label_name}
                                                label_type="single-option"
                                                control={control}
                                                rules={{
                                                  required: `${field.label_name} is required`,
                                                }}
                                                isRequired={
                                                  field.is_required === "yes"
                                                }
                                                isImportant={
                                                  field.is_important === "yes"
                                                }
                                                options={leadOwner.map(
                                                  (lead) => ({
                                                    value: lead.leadId,
                                                    label: lead.leadName,
                                                  }),
                                                )}
                                              />
                                            </Col>
                                          )}
                                          {field.label_type === "person" && (
                                            <Col md={6}>
                                              <SelectOpt
                                                name={field.id}
                                                label_name={field.label_name}
                                                label_type="single-option"
                                                control={control}
                                                rules={{
                                                  required: `${field.label_name} is required`,
                                                }}
                                                isRequired={
                                                  field.is_required === "yes"
                                                }
                                                isImportant={
                                                  field.is_important === "yes"
                                                }
                                                options={
                                                  allContacts.length > 0
                                                    ? allContacts
                                                    : []
                                                }
                                              />
                                            </Col>
                                          )}
                                          {field.label_type ===
                                            "organization" && (
                                              <Col md={6}>
                                                <SelectOpt
                                                  name={field.id}
                                                  label_name={field.label_name}
                                                  label_type="single-option"
                                                  control={control}
                                                  rules={{
                                                    required: `${field.label_name} is required`,
                                                  }}
                                                  isRequired={
                                                    field.is_required === "yes"
                                                  }
                                                  isImportant={
                                                    field.is_important === "yes"
                                                  }
                                                // options={company}
                                                />
                                              </Col>
                                            )}
                                          {field.label_type === "time" && (
                                            <Col md={6}>
                                              <Time
                                                name={field.id}
                                                label_name={field.label_name}
                                                label_type={field.label_type}
                                                control={control}
                                                isRequired={
                                                  field.is_required === "yes"
                                                }
                                                isImportant={
                                                  field.is_important === "yes"
                                                }
                                              />
                                            </Col>
                                          )}
                                          {field.label_type ===
                                            "time-range" && (
                                              <Col md={6}>
                                                <Time
                                                  name={field.id}
                                                  label_name={field.label_name}
                                                  label_type={field.label_type}
                                                  control={control}
                                                  rules={{
                                                    required:
                                                      field.is_required ===
                                                      "yes" &&
                                                      "Working hours are required",
                                                  }}
                                                  isRequired={
                                                    field.is_required === "yes"
                                                  }
                                                  isImportant={
                                                    field.is_important === "yes"
                                                  }
                                                />
                                              </Col>
                                            )}

                                          {field.label_type === "phone" && (
                                            <Col md={6}>
                                              <PhoneNumberInput
                                                name={field.id}
                                                label_name={field.label_name}
                                                label_type={field.label_type}
                                                control={control}
                                                rules={{
                                                  required:
                                                    field.is_required ===
                                                    "yes" &&
                                                    "Phone number is required",
                                                }}
                                                isRequired={
                                                  field.is_required === "yes"
                                                }
                                                validatePhoneNumber={
                                                  validatePhoneNumber
                                                }
                                                isImportant={
                                                  field.is_important === "yes"
                                                }
                                              />
                                            </Col>
                                          )}
                                          {field.label_type === "date" && (
                                            <Col md={6}>
                                              <DateField
                                                name={field.id}
                                                label_name={field.label_name}
                                                label_type={field.label_type}
                                                control={control}
                                                rules={{
                                                  required:
                                                    field.is_required ===
                                                    "yes" &&
                                                    "Date is required",
                                                }}
                                                isRequired={
                                                  field.is_required === "yes"
                                                }
                                                isImportant={
                                                  field.is_important === "yes"
                                                }
                                              />
                                            </Col>
                                          )}
                                          {field.label_type ===
                                            "date range" && (
                                              <Col md={6}>
                                                <DateField
                                                  name={field.id}
                                                  label_name={field.label_name}
                                                  label_type={field.label_type}
                                                  control={control}
                                                  rules={{
                                                    required:
                                                      field.is_required ===
                                                      "yes" &&
                                                      "Date Range number is required",
                                                    validate: {
                                                      validRange: (value) => {
                                                        if (
                                                          !value?.start ||
                                                          !value?.end
                                                        )
                                                          return true; // Let required handle empty values
                                                        return (
                                                          value.start <=
                                                          value.end ||
                                                          "End date must be after or same as start date"
                                                        );
                                                      },
                                                    },
                                                  }}
                                                  isRequired={
                                                    field.is_required === "yes"
                                                  }
                                                  isImportant={
                                                    field.is_important === "yes"
                                                  }
                                                />
                                              </Col>
                                            )}
                                          {field.label_type ===
                                            "multiple-option" && (
                                              <Col md={6}>
                                                <MultipleOpt
                                                  name={field.id}
                                                  label_name={field.label_name}
                                                  label_type={field.label_type}
                                                  control={control}
                                                  isRequired={
                                                    field.is_required === "yes"
                                                      ? true
                                                      : false
                                                  }
                                                  isImportant={
                                                    field.is_important === "yes"
                                                      ? true
                                                      : false
                                                  }
                                                  options={
                                                    field.option &&
                                                    JSON.parse(field.option)
                                                  }
                                                  rules={{
                                                    required:
                                                      field.is_required ===
                                                      "yes" &&
                                                      "Teams is required",
                                                  }}
                                                />
                                              </Col>
                                            )}
                                        </React.Fragment>
                                      );
                                    })}
                                </Row>
                              </div>
                            </React.Fragment>
                          );
                        })}

                      {ungrouped?.length !== 0 && <hr />}
                      <Row
                        className="w-100 ms-0 mt-3"
                        style={{ paddingLeft: "20px" }}
                      >
                        {ungrouped &&
                          ungrouped.length !== 0 &&
                          ungrouped.map((field) => {
                            return (
                              <React.Fragment key={field.id}>
                                {field.label_type === "text" && (
                                  <Col md={6}>
                                    <Text
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      rules={{
                                        required: `${field.label_name} is required`,
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                    />
                                  </Col>
                                )}
                                {field.label_type === "monetary" && (
                                  <Col md={6}>
                                    <Text
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      rules={{
                                        required: `${field.label_name} is required`,
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                    />
                                  </Col>
                                )}
                                {field.label_type === "largeText" && (
                                  <Col md={6}>
                                    <Text
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      rules={{
                                        required: `${field.label_name} is required`,
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                    />
                                  </Col>
                                )}
                                {field.label_type === "numerical" && (
                                  <Col md={6}>
                                    <Text
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      rules={{
                                        required: `${field.label_name} is required`,
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                    />
                                  </Col>
                                )}
                                {field.label_type === "address" && (
                                  <Col md={6}>
                                    <Text
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      rules={{
                                        required: `${field.label_name} is required`,
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                    />
                                  </Col>
                                )}
                                {field.label_type === "single-option" && (
                                  <Col md={6}>
                                    <SelectOpt
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      rules={{
                                        required: `${field.label_name} is required`,
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                      options={
                                        field.option !== null && field.option
                                      }
                                    />
                                  </Col>
                                )}
                                {field.label_type === "user" && (
                                  <Col md={6}>
                                    <SelectOpt
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      control={control}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      rules={{
                                        required: `${field.label_name} is required`,
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                      options={leadOwner}
                                    />
                                  </Col>
                                )}
                                {field.label_type === "person" && (
                                  <Col md={6}>
                                    <SelectOpt
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      control={control}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      rules={{
                                        required: `${field.label_name} is required`,
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                      options={
                                        allContacts.length > 0
                                          ? allContacts
                                          : []
                                      }
                                    />
                                  </Col>
                                )}
                                {field.label_type === "organization" && (
                                  <Col md={6}>
                                    <SelectOpt
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      rules={{
                                        required: `${field.label_name} is required`,
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                    //  options={company}
                                    />
                                  </Col>
                                )}
                                {field.label_type === "time" && (
                                  <Col md={6}>
                                    <Time
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                    />
                                  </Col>
                                )}
                                {field.label_type === "time-range" && (
                                  <Col md={6}>
                                    <Time
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      // rules={{
                                      //   required:
                                      //     field.is_required === "yes" &&
                                      //     "Working hours are required",
                                      //   validate: {
                                      //     validRange: (value) => {
                                      //       if (!value?.start || !value?.end)
                                      //         return true // Let required handle empty values
                                      //       return (
                                      //         value.start < value.end ||
                                      //         "End time must be after start time"
                                      //       )
                                      //     },
                                      //   },
                                      // }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                    />
                                  </Col>
                                )}

                                {field.label_type === "phone" && (
                                  <Col md={6}>
                                    <PhoneNumberInput
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      rules={{
                                        required:
                                          field.is_required === "yes" &&
                                          "Phone number is required",
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                      validatePhoneNumber={validatePhoneNumber}
                                    />
                                  </Col>
                                )}
                                {field.label_type === "date" && (
                                  <Col md={6}>
                                    <DateField
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      rules={{
                                        required:
                                          field.is_required === "yes" &&
                                          "Date is required",
                                      }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                    />
                                  </Col>
                                )}
                                {field.label_type === "date range" && (
                                  <Col md={6}>
                                    <DateField
                                      name={field.id}
                                      label_name={field.label_name}
                                      label_type={field.label_type}
                                      labelClass="mb-3 focus-z-index position-relative"
                                      control={control}
                                      // rules={{
                                      //   required:
                                      //     field.is_required === "yes" &&
                                      //     "Date Range number is required",
                                      //   validate: {
                                      //     validRange: (value) => {
                                      //       if (!value?.start || !value?.end)
                                      //         return true // Let required handle empty values
                                      //       return (
                                      //         value.start <= value.end ||
                                      //         "End date must be after or same as start date"
                                      //       )
                                      //     },
                                      //   },
                                      // }}
                                      isRequired={field.is_required === "yes"}
                                      isImportant={field.is_important === "yes"}
                                    />
                                  </Col>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </Row>
                    </div>

                    <Row id="itemsDetailsSection">
                      <h3
                        className=" mb-3"
                        style={{
                          whiteSpace: "nowrap",
                          fontWeight: 300,
                          fontSize: "19px",
                        }}
                      >
                        {" "}
                        Items Details
                      </h3>

                      <Col md={12} className="pe-0">
                        <div className="table-responsive quatationsTables mb-3 ">
                          <table className="table mb-0">
                            <thead>
                              <tr>
                                <th
                                  className="text-start"
                                  style={{ width: "15%" }}
                                >
                                  Items Name
                                </th>
                                <th
                                  style={{ width: "10%" }}
                                  className="text-end"
                                >
                                  Quantity
                                </th>
                                <th
                                  style={{ width: "12%" }}
                                  className="text-end"
                                >
                                  Rate{" "}
                                </th>
                                {customer?.isRegistered == false ? " ":<th
                                  style={{ width: "8%" }}
                                  className="text-end"
                                >
                                  {" "}
                                  GST(
                                  <i className="bi bi-percent"></i>){" "}
                                </th>}
                                <th
                                  style={{ width: "16%" }}
                                  className="text-end"
                                >
                                  {" "}
                                  Discount(
                                  <i className="bi bi-percent"></i>/
                                  <FontAwesomeIcon
                                          icon={faIndianRupeeSign}
                                          className="fs-11"
                                        />)
                                </th>
                                <th
                                  style={{ width: "11%" }}
                                  className="text-end"
                                >
                                  Amount{" "}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {fields.map((field, index) => {
                                const hasDescription =
                                  getValues(`items.${index}.description`) &&
                                  getValues(
                                    `items.${index}.description`,
                                  ).trim() !== "";
                                const shouldShowDescription =
                                  showTextareaIndex === index || hasDescription;

                                return (
                                  <>
                                    <tr key={field.id}>
                                      <td className="text-start">
                                        <div>
                                          <Controller
                                            name={`items.${index}.item`}
                                            control={control}
                                            render={({ field: formField }) => (
                                              <div className="mt-0 focus-z-index">
                                                <Select
                                                  {...formField}
                                                  options={getAvailableOptions(
                                                    index,
                                                  )}
                                                  placeholder="Select Item"
                                                  styles={customStyle}
                                                  value={formField.value}
                                                  onChange={(selected) =>
                                                    handleProductSelect(
                                                      selected,
                                                      index,
                                                      formField.onChange,
                                                    )
                                                  }
                                                />
                                              </div>
                                            )}
                                          />

                                          {errors.items?.[index]?.item && (
                                            <p
                                              className="text-danger mt-1"
                                              style={{ fontSize: "12px" }}
                                            >
                                              {errors.items[index].item.message}
                                            </p>
                                          )}

                                          <div
                                            className="d-flex align-items-center mt-2"
                                            onClick={() =>
                                              handleChangeTextArea(index)
                                            }
                                          >
                                            <span
                                              className="me-2"
                                              style={{
                                                cursor: "pointer",
                                                color: "#0099ff",
                                                fontSize: "12px",
                                              }}
                                            >
                                              <FontAwesomeIcon
                                                icon={
                                                  shouldShowDescription
                                                    ? faXmark
                                                    : faPlus
                                                }
                                                className="me-1"
                                              />{" "}
                                              {shouldShowDescription
                                                ? "Remove"
                                                : "Add Description"}
                                            </span>
                                          </div>
                                        </div>
                                      </td>
                                      <td>
                                        <Controller
                                          name={`items.${index}.quantity`}
                                          control={control}
                                          render={({ field: qtyField }) => (
                                            <input
                                              {...qtyField}
                                              type="number"
                                              className="normalInputItem text-end"
                                              placeholder="00"
                                              // onChange={(e) => qtyField.onChange(Number.parseInt(e.target.value) || 0)}
                                              onChange={(e) => {
                                                let value = e.target.value;

                                                value = value.replace(
                                                  /[^0-9.]/g,
                                                  "",
                                                );

                                                const parts = value.split(".");
                                                if (parts.length > 2) {
                                                  value =
                                                    parts[0] +
                                                    "." +
                                                    parts.slice(1).join("");
                                                }

                                                if (
                                                  value.length > 1 &&
                                                  value.startsWith("0") &&
                                                  !value.startsWith("0.")
                                                ) {
                                                  value = value.replace(
                                                    /^0+/,
                                                    "",
                                                  );
                                                }

                                                qtyField.onChange(
                                                  value === ""
                                                    ? ""
                                                    : Number.parseFloat(value),
                                                );
                                              }}
                                            />
                                          )}
                                        />
                                        {errors.items?.[index]?.quantity && (
                                          <p
                                            className="text-danger mt-1"
                                            style={{ fontSize: "12px" }}
                                          >
                                            {
                                              errors.items[index].quantity
                                                .message
                                            }
                                          </p>
                                        )}
                                      </td>
                                      <td>
                                        <Controller
                                          name={`items.${index}.rate`}
                                          control={control}
                                          render={({ field: rateField }) => (
                                            <input
                                              {...rateField}
                                              type="number"
                                              className="normalInputItem text-end"
                                              placeholder="00.00"
                                              onChange={(e) => {
                                                let value = e.target.value;

                                                value = value.replace(
                                                  /[^0-9.]/g,
                                                  "",
                                                );

                                                const parts = value.split(".");
                                                if (parts.length > 2) {
                                                  value =
                                                    parts[0] +
                                                    "." +
                                                    parts.slice(1).join("");
                                                }

                                                if (
                                                  value.length > 1 &&
                                                  value.startsWith("0") &&
                                                  !value.startsWith("0.")
                                                ) {
                                                  value = value.replace(
                                                    /^0+/,
                                                    "",
                                                  );
                                                }

                                                rateField.onChange(
                                                  value === ""
                                                    ? ""
                                                    : Number.parseFloat(value),
                                                );
                                              }}
                                            />
                                          )}
                                        />
                                        {errors.items?.[index]?.rate && (
                                          <p
                                            className="text-danger mt-1"
                                            style={{ fontSize: "12px" }}
                                          >
                                            {errors.items[index].rate.message}
                                          </p>
                                        )}
                                      </td>

                                      {customer?.isRegistered == false ? " ":<td>
                                        <Controller
                                          name={`items.${index}.gst`}
                                          control={control}
                                          render={({ field: gstField }) => (
                                            <input
                                              {...gstField}
                                              type="number"
                                              className="normalInputItem text-end"
                                              placeholder="0.00"
                                              style={{ paddingRight: 23 }}
                                              readOnly
                                            />
                                          )}
                                        />
                                        <i
                                          className="bi bi-percent"
                                          style={{
                                            position: "absolute",
                                            marginLeft: "-20px",
                                            marginTop: "8px",
                                          }}
                                        ></i>
                                        {errors.items?.[index]?.gst && (
                                          <p
                                            className="text-danger mt-1"
                                            style={{ fontSize: "12px" }}
                                          >
                                            {errors.items[index].gst.message}
                                          </p>
                                        )}
                                      </td>}
                                      <td>
                                        <div className="d-flex gap-1">
                                          <Controller
                                            name={`items.${index}.discount`}
                                            control={control}
                                            render={({
                                              field: discountField,
                                            }) => (
                                              <input
                                                {...discountField}
                                                type="number"
                                                inputMode="decimal"
                                                className="normalInputItem text-end"
                                                placeholder="0.00"
                                                value={
                                                  discountField.value ?? ""
                                                }
                                                onChange={(e) => {
                                                  let value = e.target.value;

                                                  value = value.replace(
                                                    /[^0-9.]/g,
                                                    "",
                                                  );

                                                  const parts =
                                                    value.split(".");
                                                  if (parts.length > 2) {
                                                    value =
                                                      parts[0] +
                                                      "." +
                                                      parts.slice(1).join("");
                                                  }

                                                  if (
                                                    value.length > 1 &&
                                                    value.startsWith("0") &&
                                                    !value.startsWith("0.")
                                                  ) {
                                                    value = value.replace(
                                                      /^0+/,
                                                      "",
                                                    );
                                                  }

                                                  discountField.onChange(
                                                    value === ""
                                                      ? ""
                                                      : Number.parseFloat(
                                                        value,
                                                      ),
                                                  );
                                                }}
                                              />
                                            )}
                                          />

                                          <Controller
                                            name={`items.${index}.discountType`}
                                            control={control}
                                            render={({ field: typeField }) => (
                                              <div style={{ width: "100px" }}>
                                                <Select
                                                  {...typeField}
                                                  options={options}
                                                  classNamePrefix=""
                                                  isSearchable={false}
                                                  onChange={(
                                                    selectedOption,
                                                  ) => {
                                                    typeField.onChange(
                                                      selectedOption.value,
                                                    );
                                                  }}
                                                  value={options.find(
                                                    (opt) =>
                                                      opt.value ===
                                                      typeField.value,
                                                  )}
                                                  styles={{
                                                    control: (base) => ({
                                                      ...base,
                                                      minHeight: 35,
                                                      paddingLeft: 4,
                                                    }),
                                                    indicatorsContainer: (
                                                      base,
                                                    ) => ({
                                                      ...base,
                                                      paddingRight: 4,
                                                    }),
                                                    dropdownIndicator: (
                                                      base,
                                                    ) => ({
                                                      ...base,
                                                      padding: 2,
                                                    }),
                                                  }}
                                                />
                                              </div>
                                            )}
                                          />
                                        </div>
                                        {errors.items?.[index]?.discount && (
                                          <p
                                            className="text-danger mt-1"
                                            style={{ fontSize: "12px" }}
                                          >
                                            {
                                              errors.items[index].discount
                                                .message
                                            }
                                          </p>
                                        )}
                                      </td>
                                      <td className="text-end">
                                        <div className="d-flex gap-2 align-items-center justify-content-center">
                                          <div
                                            className="fs-14 text-end normalInputItem"
                                            style={{
                                              fontWeight: 600,
                                              fontSize: 14,
                                            }}
                                          >
                                            {/* <FontAwesomeIcon icon={faIndianRupeeSign} /> {calculateItemAmount(index)} */}
                                            {formatToINR(
                                              calculateItemAmount(index) ||
                                              "0.00",
                                            )}
                                          </div>

                                          {fields.length > 1 && (
                                            <>
                                              <style>
                                                {`
                                            .abc {
                                              width: 1px;
                                              background: lightblue;
                                              height: 28px;
                                              margin-bottom: 28px !important;
                                            }
                                          `}
                                              </style>
                                              <p
                                                className="abc"
                                                style={{
                                                  width: 1,
                                                  background: "lightblue",
                                                  height: 22,
                                                }}
                                              ></p>
                                              <Button
                                                variant="link"
                                                className="text-danger ms-0 p-0 "
                                                style={{ marginBottom: "28px" }}
                                                onClick={() =>
                                                  handleDeleteRow(index)
                                                }
                                                title="Delete Row"
                                              >
                                                <FontAwesomeIcon
                                                  icon={faXmark}
                                                />
                                              </Button>
                                            </>
                                          )}
                                        </div>
                                      </td>
                                    </tr>

                                    {shouldShowDescription && (
                                      <tr style={noFillRowStyle}>
                                        <td
                                          colSpan={6}
                                          className="p-0 border-0"
                                        >
                                          <div className="px-1 py-2">
                                            <Controller
                                              name={`items.${index}.description`}
                                              control={control}
                                              render={({
                                                field: descField,
                                              }) => (
                                                <QuotationEditor
                                                  value={descField.value}
                                                  onChange={descField.onChange}
                                                  placeholder="Enter a description about item."
                                                  minHeight={57}
                                                  onBlur={() =>
                                                    handleBlur(index)
                                                  }
                                                />
                                              )}
                                            />
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </>
                                );
                              })}
                              <tr>
                                <th className="text-start bg-transparent vertical-top">
                                  <div>
                                    <a
                                      className="mb-0 fs-13 d-flex gap-2 align-items-center"
                                      onClick={addRow}
                                      style={{
                                        borderRadius: 4,
                                        color: " rgb(89 86 86)",
                                        background: "#f9f9fb",
                                        padding: "8px 10px",
                                        fontSize: 13,
                                        width: "fit-content",
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faPlus}
                                        style={{
                                          border: "1px solid #0099ff",
                                          background: "#0099ff",
                                          color: "#fff",
                                          padding: 4,
                                          borderRadius: "50%",
                                          height: 11,
                                          width: 11,
                                        }}
                                      />{" "}
                                      Add New Row
                                    </a>
                                  </div>
                                </th>
                                <th
                                  colSpan={5}
                                  className="text-start bg-transparent"
                                ></th>
                              </tr>
                              <tr>
                                <th
                                  colSpan={2}
                                  className="text-start bg-transparent border-0 vertical-top"
                                  style={{ verticalAlign: "bottom" }}
                                >
                                  <div className="mt-0">
                                    <p
                                      className="ps-1 mb-2 fs-13"
                                      style={{ fontWeight: 400 }}
                                    >
                                      Customer Note
                                    </p>
                                    <Controller
                                      name="customerNote"
                                      control={control}
                                      render={({ field }) => (
                                        <QuotationEditor
                                          value={field.value}
                                          onChange={field.onChange}
                                          placeholder="Looking forward for your business."
                                          minHeight={160}
                                          maxHeight={160}
                                        />
                                      )}
                                    />
                                  </div>
                                </th>

                                <th
                                  colSpan={4}
                                  className="text-start bg-transparent border-0 "
                                >
                                  <div
                                    className=" h-100 p-3"
                                    style={{
                                      borderRadius: 5,
                                      background: "#f9f9fb",
                                    }}
                                  >
                                    <div className="row sub-total mb-2 text-semibold ">
                                      <div className="total-label fs-14 fe-500 col-md-6 d-flex align-items-center">
                                        Sub Total
                                      </div>
                                      <div className="total-amount text-end fs-14 fw-500 text-semibold col-md-6 d-flex gap-1 align-items-center justify-content-end">
                                        {formatToINR(calculateSubTotal() || 0)}
                                      </div>
                                    </div>

                                    <div className="row sub-total mb-1">
                                      <div className="total-label col-md-4 fs-13 fw-400 text-secondary d-flex align-items-center">
                                        Special Discount
                                      </div>
                                      <div className="total-amount text-center col-md-5 d-flex gap-1">
                                        <div>
                                          <Controller
                                            name="specialDiscount"
                                            control={control}
                                            render={({
                                              field: discountField,
                                            }) => (
                                              <input
                                                style={{ width: "130px" }}
                                                {...discountField}
                                                type="number"
                                                className="normalInput text-end "
                                                placeholder="00"
                                                onChange={(e) =>
                                                  discountField.onChange(
                                                    Number.parseFloat(
                                                      e.target.value,
                                                    ) || 0,
                                                  )
                                                }
                                              />
                                            )}
                                          />
                                          {errors.specialDiscount && (
                                            <p
                                              className="text-danger mt-1"
                                              style={{ fontSize: "12px" }}
                                            >
                                              {errors.specialDiscount.message}
                                            </p>
                                          )}
                                        </div>

                                        <Controller
                                          name="specialDiscountType"
                                          control={control}
                                          render={({ field: typeField }) => (
                                            <div style={{ width: "80px" }}>
                                              <Select
                                                {...typeField}
                                                options={discountTypeOptions}
                                                classNamePrefix=""
                                                isSearchable={false}
                                                onChange={(selectedOption) => {
                                                  typeField.onChange(
                                                    selectedOption.value,
                                                  );
                                                }}
                                                value={discountTypeOptions.find(
                                                  (opt) =>
                                                    opt.value ===
                                                    typeField.value,
                                                )}
                                                styles={{
                                                  control: (base) => ({
                                                    ...base,
                                                    minHeight: 35,
                                                    paddingLeft: 4,
                                                    borderColor: "lightblue",
                                                    borderRadius: 4,
                                                    cursor: "pointer",
                                                  }),
                                                  dropdownIndicator: (
                                                    base,
                                                  ) => ({
                                                    ...base,
                                                    padding: 2,
                                                  }),
                                                  indicatorsContainer: (
                                                    base,
                                                  ) => ({
                                                    ...base,
                                                    paddingRight: 4,
                                                  }),
                                                }}
                                              />
                                            </div>
                                          )}
                                        />
                                      </div>

                                      <div className="total-amount text-end fs-13 fw-400 text-secondary col-md-3 d-flex align-items-center justify-content-end">
                                        -{" "}
                                        {/* <FontAwesomeIcon
                                                                      icon={faIndianRupeeSign}
                                                                      style={{ marginRight: "3px" }}
                                                                    />{" "} */}
                                        {formatToINR(calculateDiscountAmount())}
                                      </div>
                                    </div>

                                    <div className="row sub-total mb-1">
                                      <div className="total-label col-md-4 d-flex align-items-center fs-13 fw-400 text-secondary">
                                        Shipping Charge
                                      </div>

                                      {/* SAME WIDTH AS SPECIAL DISCOUNT */}
                                      <div className="total-amount text-center col-md-5 d-flex align-items-center">
                                        <Controller
                                          name="shippingCharge"
                                          control={control}
                                          render={({ field }) => (
                                            <input
                                              {...field}
                                              type="number"
                                              className="normalInput text-end"
                                              placeholder="00"
                                              style={{ maxWidth: "130px" }}
                                              onChange={(e) =>
                                                field.onChange(
                                                  Number.parseFloat(
                                                    e.target.value,
                                                  ) || 0,
                                                )
                                              }
                                            />
                                          )}
                                        />
                                      </div>

                                      {/* SAME WIDTH */}
                                      <div className="total-amount text-end fs-13 fw-400 text-secondary col-md-3 d-flex align-items-center justify-content-end">
                                        {formatToINR(
                                          watch("shippingCharge") || 0,
                                        )}
                                      </div>
                                    </div>

                                    {Object.entries(calculateGroupedGST())
                                      .filter(
                                        ([gstRate]) => Number(gstRate) > 0,
                                      )
                                      .sort(([a], [b]) => Number(a) - Number(b)) // GST percentage ascending
                                      .map(([gstRate, gstAmount]) => (
                                        <div
                                          className="row sub-total mb-1"
                                          key={gstRate}
                                        >
                                          <div className="total-label col-md-4 d-flex align-items-center fw-400 text-secondary fs-13">
                                            GST ({gstRate}%)
                                          </div>
                                          <div className="total-amount text-end fs-13 fw-400 text-secondary col-md-8  d-flex align-items-center justify-content-end">
                                            {formatToINR(gstAmount.toFixed(2))}
                                          </div>
                                        </div>
                                      ))}

                                    <div className="row sub-total ">
                                      <div className="total-label col-md-6 fs-13 fw-400 text-secondary">
                                        Round Off
                                        {/* (
                                                                    <FontAwesomeIcon
                                                                      icon={faIndianRupeeSign}
                                                                      className="fs-13"
                                                                    />
                                                                    ) */}
                                      </div>
                                      <div className="total-amount text-end col-md-6 fs-13 text-secondary fw-400">
                                        {formatToINR(roundOff.toFixed(2))}
                                      </div>
                                    </div>

                                    <hr />

                                    <div className="row sub-total ">
                                      <div className="total-label fs-14 fw-500 col-md-6">
                                        Grand Total
                                        {/* (
                                                                    <FontAwesomeIcon
                                                                      icon={faIndianRupeeSign}
                                                                      className="fs-13"
                                                                    />
                                                                    ) */}
                                      </div>
                                      <div className="total-amount text-end fs-14 fw-500 col-md-6">
                                        {formatToINR(grandTotal)}
                                      </div>
                                    </div>
                                  </div>
                                </th>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </Col>
                    </Row>

                    <hr />

                    <Row id="termsAndConditionsSec">
                      <Col md={8}>
                        <h3
                          className=" mb-3"
                          style={{
                            whiteSpace: "nowrap",
                            fontWeight: 300,
                            fontSize: "19px",
                          }}
                        >
                          {" "}
                          Terms & Conditions{" "}
                        </h3>

                        <Form.Group
                          className="mb-3"
                          controlId="formTermsConditions"
                        >
                          <Controller
                            name="termsConditions"
                            control={control}
                            render={({ field }) => (
                              <QuotationEditor
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Enter the terms and conditions of your business to be displayed in your transaction"
                                minHeight={81}
                              />
                            )}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4} className="mb-3">
                        <h3
                          className=" mb-3"
                          style={{
                            whiteSpace: "nowrap",
                            fontWeight: 300,
                            fontSize: "19px",
                          }}
                        >
                          {" "}
                          Add Attachment{" "}
                        </h3>
                        <div
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          // onClick={handleClickButton}
                          style={{
                            border: "2px dashed #ccc",
                            borderRadius: "10px",
                            padding: "10px 20px",
                            textAlign: "center",
                            background: isDragging ? "#e0f3ff" : "#f9f9f9",
                            width: "100%",
                            maxWidth: "400px",
                            cursor: "pointer",
                          }}
                        >
                          <p style={{ marginBottom: "5px", color: "#666" }}>
                            {file ? (
                              <>
                                {`Selected: ${file.name}`}{" "}
                                <IconXboxX
                                  stroke={2}
                                  size={18}
                                  style={{ stroke: "red", cursor: "pointer" }}
                                  onClick={() => setFile(null)}
                                />
                              </>
                            ) : (
                              "Drag & drop or click to upload a file"
                            )}
                          </p>

                          <button
                            type="button"
                            onClick={handleClickButton}
                            style={{
                              padding: "5px 20px",
                              backgroundColor: "#0099ff",
                              color: "#fff",
                              borderRadius: "5px",
                              cursor: "pointer",
                              border: "none",
                              fontSize: "16px",
                            }}
                          >
                            <FontAwesomeIcon icon={faCloudUpload} /> Browse File
                          </button>

                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleChange}
                            style={{ display: "none" }}
                          />
                        </div>
                        <div
                          style={{
                            // marginTop: "10px",
                            width: "100%",
                            maxWidth: "400px",
                            textAlign: "center",
                          }}
                        >
                          <p
                            style={{
                              color: "#a5a5a5",
                              fontSize: "11px",
                              fontWeight: "400",
                              margin: 0,
                            }}
                          >
                            Accepted file types are .jpg, .png, .pdf and .jpeg,
                            max file size 5MB
                          </p>
                        </div>
                        {existingFile?.url && !file && (
                          <div
                            style={{
                              marginTop: "15px",
                              padding: "10px",
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              background: "#f1f1f1",
                              textAlign: "center",
                            }}
                          >
                            <a
                              href={existingFile.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#007bff",
                                fontWeight: "500",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              Click to watch old file
                            </a>
                          </div>
                        )}
                      </Col>
                      {/* <Col md={4} className="mb-3">
                      <h3
                        className=" mb-3"
                        style={{
                          whiteSpace: "nowrap",
                          fontWeight: 300,
                          fontSize: "19px",
                        }}
                      >
                        Add Attachment
                      </h3>

                      Browse File (always empty by default)
                      <div
                        style={{
                          border: "2px dashed #ccc",
                          borderRadius: "10px",
                          padding: "10px 20px",
                          textAlign: "center",
                          background: "#f9f9f9",
                          width: "100%",
                          maxWidth: "400px",
                        }}
                      >
                        <p style={{ marginBottom: "5px", color: "#666" }}>
                          {file
                            ? `Selected: ${file.name}`
                            : "Drag & drop or click to upload a file"}
                        </p>

                        <button
                          type="button"
                          onClick={handleClickButton}
                          style={{
                            padding: "5px 20px",
                            backgroundColor: "#0099ff",
                            color: "#fff",
                            borderRadius: "5px",
                            cursor: "pointer",
                            border: "none",
                            fontSize: "16px",
                          }}
                        >
                          <FontAwesomeIcon icon={faCloudUpload} /> Browse File
                        </button>

                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleChange}
                          style={{ display: "none" }}
                        />
                      </div>

                      <div
                        style={{
                          width: "100%",
                          maxWidth: "400px",
                          textAlign: "center",
                        }}
                      >
                        <p
                          style={{
                            color: "#a5a5a5",
                            fontSize: "11px",
                            fontWeight: "400",
                            margin: 0,
                          }}
                        >
                          Accepted file types are .jpg, .png, .pdf, max file size
                          5MB
                        </p>
                      </div>

                      {existingFile?.url && !file && (
                        <div
                          style={{
                            marginTop: "15px",
                            padding: "10px",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            background: "#f1f1f1",
                            textAlign: "center",
                          }}
                        >
                          <a
                            href={existingFile.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#007bff",
                              fontWeight: "500",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            Click to watch old file
                          </a>
                        </div>
                      )}
                    </Col> */}
                    </Row>

                    {/* <hr /> */}
                  </Row>
                  <div className="zb-txn-form mb-4 text-center"></div>

                  <div
                    className="bg-white position-sticky"
                    style={{
                      // paddingRight: "2rem",
                      // height: 75,
                      // bottom: "0",
                      // right: "1.4rem",
                      // display: "flex",
                      // alignItems: "center",
                      // justifyContent: "end",
                      // width: "92.9%",
                      // zIndex: 11,
                      // borderTop: "1px solid lightblue",
                      paddingTop: "13px",
                      paddingBottom: "15px",
                      paddingRight: "17px",
                      // height: 75,
                      bottom: "67.5px",
                      // right: "20.5px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "end",
                      // width: "93.4%",
                      zIndex: 11,
                      borderTop: "1px solid lightblue",
                    }}
                  >
                    <Button
                      className="btn btn-default-outline me-2"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="btn btn-primary me-2"
                      type="submit"
                      onClick={handleDraftClick}
                      disabled={grandTotal <= 0 || loading}
                    >
                      {loading ? "loading..." : " Save As Draft"}
                    </Button>
                    <Button
                      className="btn btn-default"
                      type="submit"
                      disabled={grandTotal <= 0 || loading}
                    >
                      {loading ? "loading..." : "Send To Approve"}
                    </Button>
                  </div>
                </Col>
              </Row>

              <div className="form-actions mt-4">
                <Button type="submit" variant="primary">
                  {mode === "clone" ? "Clone Quotation" : "Update Quotation"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                  className="ms-2"
                >
                  Cancel
                </Button>
              </div>
            </Form>

            <Modal
              show={show}
              onHide={handleClose}
              backdrop="static"
              keyboard={false}
            >
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 18, fontWeight: 400 }}>
                  Create Quotation Series
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <form action="" method="post" className="form-container">
                  <div className="form-check ps-1 mb-1 d-flex align-items-center ">
                    <input
                      type="text"
                      value=""
                      id="feeReceiptId"
                      name="feeReceiptId"
                      hidden
                    />
                    <input
                      type="radio"
                      name=""
                      id=""
                      className="me-2"
                      style={{ width: 17, height: 17 }}
                    />

                    <p className="form-label mt-1">NG-2025-08-100</p>
                  </div>
                  <div className="form-check mb-1 ps-1 d-flex align-items-center">
                    <input
                      type="radio"
                      name=""
                      id=""
                      className="me-2"
                      style={{ width: 17, height: 17 }}
                    />
                    <p className="form-label mt-1">NG-08-2025-100</p>
                  </div>
                  <div className="form-check ps-1 mb-1 d-flex align-items-center">
                    <input
                      type="radio"
                      name=""
                      id=""
                      className="me-2"
                      style={{ width: 17, height: 17 }}
                    />
                    <p className="form-label mt-1">NG-2025-100</p>
                  </div>
                  <div className="form-check ps-1 mb-1 d-flex align-items-center">
                    <input
                      type="radio"
                      name=""
                      id=""
                      className="me-2"
                      style={{ width: 17, height: 17 }}
                    />
                    <p className="form-label mt-1">NG-100</p>
                  </div>
                  <div className="input-div position-relative focus-z-index mt-3 mb-2">
                    <label htmlFor="incrementalNo" className="form-label">
                      Enter Incremental Number Here: <span>*</span>
                    </label>
                    <input
                      type="number"
                      name="incrementalNumber"
                      className="form-control prefix-element "
                      id="incrementalNumber"
                      placeholder="Enter Any 4 Digits"
                      required=""
                    />
                  </div>
                  <div className="input-div position-relative focus-z-index mt-3 mb-4">
                    <label htmlFor="summaryPermission" className="form-label">
                      Want Course Fee Summary? <span>*</span>
                    </label>
                    <select
                      name="summaryPermission"
                      id="summaryPermission"
                      className="form-select"
                      onChange={() => { }}
                      required=""
                    >
                      <option value="1" selected={true}>
                        Yes
                      </option>
                      <option value="0">No</option>
                    </select>
                  </div>
                  <Modal.Footer>
                    <Button variant="default-outline" onClick={handleClose}>
                      Close
                    </Button>
                    <Button variant="default" onClick={() => alert("Saved!")}>
                      Save Changes
                    </Button>
                  </Modal.Footer>
                </form>
              </Modal.Body>
            </Modal>
            <AddressModal
              show={showBillingModal}
              handleClose={() => setShowBillingModal(false)}
              onSubmit={handleBillingSubmit}
              contactOption={selectedContact}
              // pass current contact object
              setSelectedContact={setSelectedContact}
              title="Create New Billing Address"
              defaultFor="billing"
            />

            <AddressModal
              show={showShippingModal}
              handleClose={() => setShowShippingModal(false)}
              onSubmit={handleShippingSubmit}
              contactOption={selectedContact}
              title="Create New Shipping Address"
              defaultFor="shipping"
              setSelectedContact={setSelectedContact}
            />
          </div>
        </>
      )}
    </>
  );
};